/**
 * Экран понедельного планирования — формат «метрика × недели».
 *
 * Самодостаточный экран для проверки UX-идеи (сетка с колонкой «Итого периода»,
 * видимая воронка, live-diff сценариев, правый инспектор). Работает параллельно
 * с классическим StudioMetricsPage и ничего из него не импортирует. Логика редактирования и
 * авторасчёта (воронка, средние чеки, ЧВ, итог выручки) — зеркало боевого
 * calculateDerivedPlanValues; видимость секций — та же логика по subscription_type.
 * Маршрут: /partners/:partnerNumber/studios/:studioId/studio-metrics-grid
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { Card } from '@/components/ui'
import { ArrowLeft, ArrowRightLeft, RotateCcw, Target, TrendingUp, Lock, ChevronDown, ChevronLeft, ChevronRight, ChevronsRight, Save, Users, Loader2, History, HelpCircle, BarChart3 } from 'lucide-react'
import { OnboardingTour, type TourStep } from '@/components/onboarding/OnboardingTour'
import { useOnboardingTour } from '@/components/onboarding/useOnboardingTour'
import { getSupabase } from '@/lib/supabase'
import { getLocalIsoDate } from '@/lib/analytics/periodCalculations'
import { StudioSubscriptionType } from '@/types'
import { getChannelPresence } from '@/lib/analytics/subscriptionPresence'
import { useAuthStore, useStudioStore } from '@/store'
import { getCurrencySymbol } from '@/lib/utils'
import { BulkLeadPlanGridModal } from '@/components/BulkLeadPlanGridModal'
import { setHasUnsavedMetricsChanges } from '@/lib/unsavedChanges'
import {
  getMaxSelectablePeriodIndex,
  resolveWeeklyPlanPeriods,
  type PlanningPeriod,
} from '@/lib/weeklyPlanPeriodNavigation'

// ── Мини-гид по экрану ──────────────────────────────────────────────────────────
// Ссылка должна быть стабильной (модульный уровень) — её читает OnboardingTour.
// Шаги, чьи блоки на текущем экране не отрисованы (сценарии/сохранение/распределение
// зависят от статуса периода и прав), движок пропускает автоматически.
const WEEKLY_PLAN_TOUR_STEPS: TourStep[] = [
  {
    title: 'Планирование по неделям',
    body: 'Коротко покажем, где что находится на этом экране. Это займёт меньше минуты — нажимайте «Далее».',
  },
  {
    target: 'period',
    title: 'Период планирования',
    body: 'Здесь выбран текущий период. Стрелками ← → переключайтесь между периодами. Следующий период открывается с 4-й недели текущего.',
  },
  {
    target: 'grid',
    title: 'Сетка плана',
    body: 'Строки — метрики (маркетинг, лиды, продажи, выручка), колонки — недели периода. Введите план в ячейку — воронка, средние чеки и итоговая выручка пересчитаются автоматически. Колонка «Итого план» справа суммирует период.',
  },
  {
    target: 'prefill',
    title: 'Предзаполнение из прошлого периода',
    body: 'Кнопка «Предзаполнить данными плана прошлого периода» (в блоке «Воронка пробного») заполнит редактируемые недели планом предыдущего периода — удобная отправная точка, которую дальше корректируете вручную.',
  },
  {
    target: 'template',
    title: 'Первая неделя — шаблон',
    body: 'Пока период не начался, неделя Н1 помечена как «шаблон». Заполните её — и кнопкой «на все» под Н1 скопируете все значения сразу на остальные недели. Чтобы перенести только одну метрику, наведите на её ячейку в Н1 и нажмите кнопку копирования внутри ячейки.',
  },
  {
    target: 'bulk',
    title: 'Распределение по студиям',
    body: 'Франчайзи с несколькими студиями может задать общий план лидов и маркетинга и распределить его по студиям. Доступно, когда нет несохранённых изменений сетки.',
  },
  {
    target: 'scenarios',
    title: 'Сценарии',
    body: 'Когда период уже идёт — быстро пересчитать будущие недели: подставить средний темп завершённых недель или вернуть их к сохранённому плану. Завершённые и текущая неделя фиксируются.',
  },
  {
    target: 'save',
    title: 'Сохранение плана',
    body: 'Кнопка активна, пока есть несохранённые изменения. Сохранённый план становится целью периода, с которой сравнивается текущий.',
  },
  {
    target: 'summary',
    title: 'Итог периода',
    body: 'Сводка по деньгам: черновик или сохранённый план, изменение к цели, факт к сегодня и прогноз периода по текущему темпу.',
  },
  {
    target: 'hints',
    title: 'Подсказки из истории',
    body: 'Загрузите недельные средние прошлого периода и за 3 месяца — они появятся в метке каждой строки сетки как ориентир при планировании.',
  },
  {
    target: 'help',
    title: 'Запустить подсказку снова',
    body: 'Это краткое обучение всегда можно открыть заново этой кнопкой. Удачного планирования!',
  },
]

// ── Типы ──────────────────────────────────────────────────────────────────────
interface PlanHints {
  plan_leads_week_avg: number | null; fact_leads_week_avg: number | null; fact_leads_3mo_avg: number | null
  plan_marketing_budget_week_avg: number | null; fact_marketing_budget_week_avg: number | null; fact_marketing_budget_3mo_avg: number | null
  plan_appointments_week_avg: number | null; fact_appointments_week_avg: number | null; fact_appointments_3mo_avg: number | null
  plan_new_visitors_week_avg?: number | null; fact_new_visitors_week_avg?: number | null; fact_new_visitors_3mo_avg?: number | null
  plan_new_sales_count_week_avg: number | null; fact_new_sales_count_week_avg: number | null; fact_new_sales_count_3mo_avg: number | null
  plan_new_sales_amount_week_avg: number | null; fact_new_sales_amount_week_avg: number | null; fact_new_sales_amount_3mo_avg: number | null
  plan_new_sales_aov_week_avg: number | null; fact_new_sales_aov_week_avg: number | null; fact_new_sales_aov_3mo_avg: number | null
  plan_renewals_count_week_avg: number | null; fact_renewals_count_week_avg: number | null; fact_renewals_count_3mo_avg: number | null
  plan_renewals_amount_week_avg: number | null; fact_renewals_amount_week_avg: number | null; fact_renewals_amount_3mo_avg: number | null
  plan_renewals_aov_week_avg: number | null; fact_renewals_aov_week_avg: number | null; fact_renewals_aov_3mo_avg: number | null
  plan_upgrades_count_week_avg: number | null; fact_upgrades_count_week_avg: number | null; fact_upgrades_count_3mo_avg: number | null
  plan_upgrades_amount_week_avg: number | null; fact_upgrades_amount_week_avg: number | null; fact_upgrades_amount_3mo_avg: number | null
  plan_upgrades_aov_week_avg: number | null; fact_upgrades_aov_week_avg: number | null; fact_upgrades_aov_3mo_avg: number | null
  plan_returning_clients_count_week_avg: number | null; fact_returning_clients_count_week_avg: number | null; fact_returning_clients_count_3mo_avg: number | null
  plan_returning_clients_amount_week_avg: number | null; fact_returning_clients_amount_week_avg: number | null; fact_returning_clients_amount_3mo_avg: number | null
  plan_returning_clients_aov_week_avg: number | null; fact_returning_clients_aov_week_avg: number | null; fact_returning_clients_aov_3mo_avg: number | null
  plan_private_sessions_count_week_avg: number | null; fact_private_sessions_count_week_avg: number | null; fact_private_sessions_count_3mo_avg: number | null
  plan_private_sessions_amount_week_avg: number | null; fact_private_sessions_amount_week_avg: number | null; fact_private_sessions_amount_3mo_avg: number | null
  plan_private_sessions_aov_week_avg: number | null; fact_private_sessions_aov_week_avg: number | null; fact_private_sessions_aov_3mo_avg: number | null
  plan_gross_revenue_week_avg: number | null; fact_gross_revenue_week_avg: number | null; fact_gross_revenue_3mo_avg: number | null
  plan_membership_fees_new_amount_week_avg: number | null; fact_membership_fees_new_amount_week_avg: number | null; fact_membership_fees_new_amount_3mo_avg: number | null
  plan_membership_fees_subscription_upgrades_amount_week_avg: number | null; fact_membership_fees_subscription_upgrades_amount_week_avg: number | null; fact_membership_fees_subscription_upgrades_amount_3mo_avg: number | null
  plan_membership_fees_subscription_returning_amount_week_avg: number | null; fact_membership_fees_subscription_returning_amount_week_avg: number | null; fact_membership_fees_subscription_returning_amount_3mo_avg: number | null
  plan_new_subscriptions_count_week_avg: number | null; fact_new_subscriptions_count_week_avg: number | null; fact_new_subscriptions_count_3mo_avg: number | null
  plan_new_subscriptions_amount_week_avg: number | null; fact_new_subscriptions_amount_week_avg: number | null; fact_new_subscriptions_amount_3mo_avg: number | null
  plan_new_subscriptions_aov_week_avg: number | null; fact_new_subscriptions_aov_week_avg: number | null; fact_new_subscriptions_aov_3mo_avg: number | null
  plan_subscription_renewals_count_week_avg: number | null; fact_subscription_renewals_count_week_avg: number | null; fact_subscription_renewals_count_3mo_avg: number | null
  plan_subscription_renewals_amount_week_avg: number | null; fact_subscription_renewals_amount_week_avg: number | null; fact_subscription_renewals_amount_3mo_avg: number | null
  plan_subscription_renewals_aov_week_avg: number | null; fact_subscription_renewals_aov_week_avg: number | null; fact_subscription_renewals_aov_3mo_avg: number | null
  plan_subscription_upgrades_count_week_avg: number | null; fact_subscription_upgrades_count_week_avg: number | null; fact_subscription_upgrades_count_3mo_avg: number | null
  plan_subscription_upgrades_amount_week_avg: number | null; fact_subscription_upgrades_amount_week_avg: number | null; fact_subscription_upgrades_amount_3mo_avg: number | null
  plan_subscription_upgrades_aov_week_avg: number | null; fact_subscription_upgrades_aov_week_avg: number | null; fact_subscription_upgrades_aov_3mo_avg: number | null
  plan_subscription_returning_clients_count_week_avg: number | null; fact_subscription_returning_clients_count_week_avg: number | null; fact_subscription_returning_clients_count_3mo_avg: number | null
  plan_subscription_returning_clients_amount_week_avg: number | null; fact_subscription_returning_clients_amount_week_avg: number | null; fact_subscription_returning_clients_amount_3mo_avg: number | null
  plan_subscription_returning_clients_aov_week_avg: number | null; fact_subscription_returning_clients_aov_week_avg: number | null; fact_subscription_returning_clients_aov_3mo_avg: number | null
}

type HintValues = { plan: number | null; fact: number | null; fact3mo: number | null }
interface PlanFormData {
  marketingBudget: number
  leadsCount: number
  appointmentConversion: number
  trialVisitConversion: number
  newSalesConversion: number
  newSubscriptionsConversion: number
  avgNewSalesCheck: number
  renewalsCount: number
  avgRenewalCheck: number
  upgradesCount: number
  avgUpgradeCheck: number
  returningClientsCount: number
  avgReturningClientsCheck: number
  avgNewSubscriptionCheck: number
  avgMembershipFeeNew: number
  subscriptionRenewalsCount: number
  avgSubscriptionRenewalCheck: number
  subscriptionUpgradesCount: number
  avgSubscriptionUpgradeCheck: number
  subscriptionReturningClientsCount: number
  avgSubscriptionReturningClientsCheck: number
  avgMembershipFeeSubscriptionUpgrade: number
  avgMembershipFeeSubscriptionReturning: number
  privateSessionsCount: number
  avgPrivateSessionCheck: number
  otherIncome: number
  refundsAmount: number
}

interface WeeklyFactRow {
  week_number_in_period: number
  weekly_marketing_expenses_amount_fact?: number | null
  weekly_leads_fact?: number | null
  weekly_appointments_fact?: number | null
  weekly_new_visitors_fact?: number | null
  weekly_new_sales_fact?: number | null
  weekly_new_sales_amount_fact?: number | null
  weekly_membership_fees_new_amount_fact?: number | null
  weekly_renewals_count_fact?: number | null
  weekly_renewals_amount_fact?: number | null
  weekly_upgrades_count_fact?: number | null
  weekly_upgrades_amount_fact?: number | null
  weekly_returning_clients_count_fact?: number | null
  weekly_returning_clients_amount_fact?: number | null
  weekly_new_subscriptions_count_fact?: number | null
  weekly_new_subscriptions_amount_fact?: number | null
  weekly_membership_fees_subscription_upgrades_amount_fact?: number | null
  weekly_membership_fees_subscription_returning_amount_fact?: number | null
  weekly_subscription_renewals_count_fact?: number | null
  weekly_subscription_renewals_amount_fact?: number | null
  weekly_subscription_upgrades_count_fact?: number | null
  weekly_subscription_upgrades_amount_fact?: number | null
  weekly_subscription_returning_clients_count_fact?: number | null
  weekly_subscription_returning_clients_amount_fact?: number | null
  weekly_private_sessions_count_fact?: number | null
  weekly_private_sessions_amount_fact?: number | null
  weekly_other_income_fact?: number | null
  weekly_refunds_amount_fact?: number | null
}

// Провенанс плана недели — зеркало боевого (plan_mode / plan_source / manual_updated_*).
// Пишется по-недельно: 'manual' ставится только реально правленным руками/скопированным/
// сценарным неделям; нетронутые несут прежний источник; manual_updated_* = null для не-manual.
type WeeklyPlanMode = 'auto' | 'manual'
// 'partner_allocation' — лиды/маркетинг заданы распределением по студиям франчайзи (модалка балка).
type WeeklyPlanSource = 'first_week' | 'remaining_split' | 'avg_fact' | 'manual' | 'partner_allocation' | null

interface WeekDescriptor {
  weekNumber: number
  week_start: string
  week_end: string
  week_len_days: number
  formData: PlanFormData
  isStarted: boolean
  isCompleted: boolean
  hasSavedPlan: boolean
  factRow: WeeklyFactRow | null
  planMode: WeeklyPlanMode
  planSource: WeeklyPlanSource
  manualUpdatedAt: string | null
  manualUpdatedByUserId: string | null
}

const FACT_SELECT = `week_number_in_period, weekly_marketing_expenses_amount_fact, weekly_leads_fact,
  weekly_appointments_fact, weekly_new_visitors_fact, weekly_new_sales_fact, weekly_new_sales_amount_fact,
  weekly_membership_fees_new_amount_fact, weekly_renewals_count_fact, weekly_renewals_amount_fact,
  weekly_upgrades_count_fact, weekly_upgrades_amount_fact, weekly_returning_clients_count_fact,
  weekly_returning_clients_amount_fact, weekly_new_subscriptions_count_fact, weekly_new_subscriptions_amount_fact,
  weekly_membership_fees_subscription_upgrades_amount_fact, weekly_membership_fees_subscription_returning_amount_fact,
  weekly_subscription_renewals_count_fact, weekly_subscription_renewals_amount_fact,
  weekly_subscription_upgrades_count_fact, weekly_subscription_upgrades_amount_fact,
  weekly_subscription_returning_clients_count_fact, weekly_subscription_returning_clients_amount_fact,
  weekly_private_sessions_count_fact, weekly_private_sessions_amount_fact, weekly_other_income_fact,
  weekly_refunds_amount_fact`

// ── Helpers ────────────────────────────────────────────────────────────────────
const round2 = (v: number) => Math.round((v + Number.EPSILON) * 100) / 100
const n = (v: unknown) => Number(v) || 0
const fmtInt = (v: number) => Math.round(v).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
const fmtMoney = (v: number, sym = '₽') => `${fmtInt(v)} ${sym}`
const fmtShortDate = (iso: string) => { if (!iso) return ''; const [, m = '', d = ''] = iso.split('-'); return `${d}.${m}` }
const fmtWeekNumbersText = (weekNumbers: number[]) => {
  if (weekNumbers.length === 0) return '—'
  const sorted = [...weekNumbers].sort((a, b) => a - b)
  const contiguous = sorted.every((week, index) => index === 0 || week === sorted[index - 1] + 1)
  if (sorted.length === 1) return `неделя ${sorted[0]}`
  if (contiguous) return `недели ${sorted[0]}-${sorted[sorted.length - 1]}`
  return `недели ${sorted.join(', ')}`
}
const toIso = (date: Date) => {
  const y = date.getFullYear(), m = String(date.getMonth() + 1).padStart(2, '0'), d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}
const pct = (num: number, den: number) => (den > 0 ? round2((num / den) * 100) : 0)
const avg = (amount: number, count: number) => (count > 0 ? round2(amount / count) : 0)

const EMPTY_FORM: PlanFormData = {
  marketingBudget: 0, leadsCount: 0, appointmentConversion: 0, trialVisitConversion: 0, newSalesConversion: 0,
  newSubscriptionsConversion: 0, avgNewSalesCheck: 0, renewalsCount: 0, avgRenewalCheck: 0, upgradesCount: 0,
  avgUpgradeCheck: 0, returningClientsCount: 0, avgReturningClientsCheck: 0, avgNewSubscriptionCheck: 0,
  avgMembershipFeeNew: 0, subscriptionRenewalsCount: 0, avgSubscriptionRenewalCheck: 0, subscriptionUpgradesCount: 0,
  avgSubscriptionUpgradeCheck: 0, subscriptionReturningClientsCount: 0, avgSubscriptionReturningClientsCheck: 0,
  avgMembershipFeeSubscriptionUpgrade: 0, avgMembershipFeeSubscriptionReturning: 0, privateSessionsCount: 0,
  avgPrivateSessionCheck: 0, otherIncome: 0, refundsAmount: 0,
}

const mapPlanRow = (row: any): PlanFormData => {
  if (!row) return { ...EMPTY_FORM }
  const visitsBase = n(row.new_visitors_count_plan)
  const derivedSubConv = row.new_subscriptions_conversion_plan ??
    (visitsBase > 0 && n(row.new_subscriptions_count_plan) > 0 ? (n(row.new_subscriptions_count_plan) / visitsBase) * 100 : 0)
  return {
    marketingBudget: n(row.marketing_budget_plan),
    leadsCount: n(row.leads_count_plan),
    appointmentConversion: n(row.booking_conversion_plan),
    trialVisitConversion: n(row.visit_conversion_plan),
    newSalesConversion: n(row.sales_conversion_plan),
    newSubscriptionsConversion: n(derivedSubConv),
    avgNewSalesCheck: n(row.new_sales_aov_plan),
    renewalsCount: n(row.reneval_count_plan),
    avgRenewalCheck: n(row.reneval_aov_plan),
    upgradesCount: n(row.upgrades_count_plan),
    avgUpgradeCheck: n(row.upgrades_aov_plan),
    returningClientsCount: n(row.returning_clients_count_plan),
    avgReturningClientsCheck: n(row.returning_clients_aov_plan),
    avgNewSubscriptionCheck: n(row.new_subscriptions_aov_plan),
    avgMembershipFeeNew: n(row.new_subscriptions_count_plan) > 0 ? n(row.membership_fees_new_plan) / n(row.new_subscriptions_count_plan) : 0,
    subscriptionRenewalsCount: n(row.subscription_renewals_count_plan),
    avgSubscriptionRenewalCheck: n(row.subscription_renewals_aov_plan),
    subscriptionUpgradesCount: n(row.subscription_upgrades_count_plan),
    avgSubscriptionUpgradeCheck: n(row.subscription_upgrades_aov_plan),
    subscriptionReturningClientsCount: n(row.subscription_returning_clients_count_plan),
    avgSubscriptionReturningClientsCheck: n(row.subscription_returning_clients_aov_plan),
    avgMembershipFeeSubscriptionUpgrade: n(row.subscription_upgrades_count_plan) > 0 ? n(row.membership_fees_subscription_upgrades_plan) / n(row.subscription_upgrades_count_plan) : 0,
    avgMembershipFeeSubscriptionReturning: n(row.subscription_returning_clients_count_plan) > 0 ? n(row.membership_fees_subscription_returning_plan) / n(row.subscription_returning_clients_count_plan) : 0,
    privateSessionsCount: n(row.private_sessions_count_plan),
    avgPrivateSessionCheck: n(row.private_sessions_aov_plan),
    otherIncome: n(row.other_income_plan),
    refundsAmount: Math.abs(n(row.refunds_amount_plan)),
  }
}

// Провенанс из сохранённой строки studio_weekly_plan (зеркало боевого, StudioMetricsPage:1994).
type Provenance = Pick<WeekDescriptor, 'planMode' | 'planSource' | 'manualUpdatedAt' | 'manualUpdatedByUserId'>
const mapProvenance = (row: any): Provenance => {
  if (!row) return { planMode: 'auto', planSource: null, manualUpdatedAt: null, manualUpdatedByUserId: null }
  return {
    planMode: row.plan_mode === 'manual' ? 'manual' : 'auto',
    planSource: (row.plan_source ?? null) as WeeklyPlanSource,
    manualUpdatedAt: row.manual_updated_at ?? null,
    manualUpdatedByUserId: row.manual_updated_by_user_id ?? null,
  }
}

// Воронка — зеркало calculateDerivedPlanValues
const derive = (f: PlanFormData) => {
  const leadPrice = f.leadsCount > 0 ? round2(f.marketingBudget / f.leadsCount) : 0
  const appointmentsCount = f.leadsCount * (f.appointmentConversion / 100)
  const trialVisitsCount = appointmentsCount * (f.trialVisitConversion / 100)
  const newSalesCount = trialVisitsCount * (f.newSalesConversion / 100)
  const newSubscriptionsCount = trialVisitsCount * (f.newSubscriptionsConversion / 100)
  const newSalesAmountRegular = newSalesCount * f.avgNewSalesCheck
  const newSalesAmountSubscription = newSubscriptionsCount * f.avgNewSubscriptionCheck
  const membershipFeesNewAmount = newSubscriptionsCount * f.avgMembershipFeeNew

  const renewalsAmount = f.renewalsCount * f.avgRenewalCheck
  const upgradesAmount = f.upgradesCount * f.avgUpgradeCheck
  const returningClientsAmount = f.returningClientsCount * f.avgReturningClientsCheck
  const totalNonNewSales = f.renewalsCount + f.upgradesCount + f.returningClientsCount
  const totalNonNewAmount = renewalsAmount + upgradesAmount + returningClientsAmount
  const avgNonNewCheck = totalNonNewSales > 0 ? totalNonNewAmount / totalNonNewSales : 0

  const subscriptionRenewalsAmount = f.subscriptionRenewalsCount * f.avgSubscriptionRenewalCheck
  const subscriptionUpgradesAmount = f.subscriptionUpgradesCount * f.avgSubscriptionUpgradeCheck
  const subscriptionReturningClientsAmount = f.subscriptionReturningClientsCount * f.avgSubscriptionReturningClientsCheck
  const subscriptionNonNewSales = f.subscriptionRenewalsCount + f.subscriptionUpgradesCount + f.subscriptionReturningClientsCount
  const membershipFeesSubscriptionUpgradesAmount = f.subscriptionUpgradesCount * f.avgMembershipFeeSubscriptionUpgrade
  const membershipFeesSubscriptionReturningAmount = f.subscriptionReturningClientsCount * f.avgMembershipFeeSubscriptionReturning
  const membershipFeesExistingAmount = membershipFeesSubscriptionUpgradesAmount + membershipFeesSubscriptionReturningAmount
  const subscriptionNonNewSubscriptionsAmount = subscriptionRenewalsAmount + subscriptionUpgradesAmount + subscriptionReturningClientsAmount
  const avgSubscriptionNonNewCheck = subscriptionNonNewSales > 0 ? subscriptionNonNewSubscriptionsAmount / subscriptionNonNewSales : 0
  const subscriptionNonNewAmount = subscriptionNonNewSubscriptionsAmount + membershipFeesExistingAmount

  const privateSessionsAmount = f.privateSessionsCount * f.avgPrivateSessionCheck
  const otherIncomeAmount = f.otherIncome
  const refundsPlanAmount = f.refundsAmount > 0 ? -f.refundsAmount : f.refundsAmount
  const totalRevenue = totalNonNewAmount + newSalesAmountRegular + newSalesAmountSubscription + membershipFeesNewAmount +
    subscriptionRenewalsAmount + subscriptionUpgradesAmount + subscriptionReturningClientsAmount +
    membershipFeesExistingAmount + privateSessionsAmount + otherIncomeAmount + refundsPlanAmount

  return {
    leadPrice, appointmentsCount, trialVisitsCount, newSalesCount, newSubscriptionsCount,
    newSalesAmountRegular, newSalesAmountSubscription, membershipFeesNewAmount,
    renewalsAmount, upgradesAmount, returningClientsAmount, totalNonNewSales, totalNonNewAmount, avgNonNewCheck,
    subscriptionRenewalsAmount, subscriptionUpgradesAmount, subscriptionReturningClientsAmount,
    membershipFeesSubscriptionUpgradesAmount, membershipFeesSubscriptionReturningAmount, membershipFeesExistingAmount,
    subscriptionNonNewSales, subscriptionNonNewSubscriptionsAmount, subscriptionNonNewAmount, avgSubscriptionNonNewCheck,
    privateSessionsAmount, otherIncomeAmount, refundsPlanAmount, totalRevenue,
  }
}
type Derived = ReturnType<typeof derive>

// Факт недели как «синтетический формдата»: конверсии/чеки = отношения факт-сумм.
// derive() от него воспроизводит фактические объёмы и выручку — те же строки сетки.
const factToFormData = (r: WeeklyFactRow): PlanFormData => ({
  marketingBudget: Math.abs(n(r.weekly_marketing_expenses_amount_fact)),
  leadsCount: n(r.weekly_leads_fact),
  appointmentConversion: pct(n(r.weekly_appointments_fact), n(r.weekly_leads_fact)),
  trialVisitConversion: pct(n(r.weekly_new_visitors_fact), n(r.weekly_appointments_fact)),
  newSalesConversion: pct(n(r.weekly_new_sales_fact), n(r.weekly_new_visitors_fact)),
  newSubscriptionsConversion: pct(n(r.weekly_new_subscriptions_count_fact), n(r.weekly_new_visitors_fact)),
  avgNewSalesCheck: avg(n(r.weekly_new_sales_amount_fact), n(r.weekly_new_sales_fact)),
  renewalsCount: n(r.weekly_renewals_count_fact),
  avgRenewalCheck: avg(n(r.weekly_renewals_amount_fact), n(r.weekly_renewals_count_fact)),
  upgradesCount: n(r.weekly_upgrades_count_fact),
  avgUpgradeCheck: avg(n(r.weekly_upgrades_amount_fact), n(r.weekly_upgrades_count_fact)),
  returningClientsCount: n(r.weekly_returning_clients_count_fact),
  avgReturningClientsCheck: avg(n(r.weekly_returning_clients_amount_fact), n(r.weekly_returning_clients_count_fact)),
  avgNewSubscriptionCheck: avg(n(r.weekly_new_subscriptions_amount_fact), n(r.weekly_new_subscriptions_count_fact)),
  avgMembershipFeeNew: avg(n(r.weekly_membership_fees_new_amount_fact), n(r.weekly_new_subscriptions_count_fact)),
  subscriptionRenewalsCount: n(r.weekly_subscription_renewals_count_fact),
  avgSubscriptionRenewalCheck: avg(n(r.weekly_subscription_renewals_amount_fact), n(r.weekly_subscription_renewals_count_fact)),
  subscriptionUpgradesCount: n(r.weekly_subscription_upgrades_count_fact),
  avgSubscriptionUpgradeCheck: avg(n(r.weekly_subscription_upgrades_amount_fact), n(r.weekly_subscription_upgrades_count_fact)),
  subscriptionReturningClientsCount: n(r.weekly_subscription_returning_clients_count_fact),
  avgSubscriptionReturningClientsCheck: avg(n(r.weekly_subscription_returning_clients_amount_fact), n(r.weekly_subscription_returning_clients_count_fact)),
  avgMembershipFeeSubscriptionUpgrade: avg(n(r.weekly_membership_fees_subscription_upgrades_amount_fact), n(r.weekly_subscription_upgrades_count_fact)),
  avgMembershipFeeSubscriptionReturning: avg(n(r.weekly_membership_fees_subscription_returning_amount_fact), n(r.weekly_subscription_returning_clients_count_fact)),
  privateSessionsCount: n(r.weekly_private_sessions_count_fact),
  avgPrivateSessionCheck: avg(n(r.weekly_private_sessions_amount_fact), n(r.weekly_private_sessions_count_fact)),
  otherIncome: n(r.weekly_other_income_fact),
  refundsAmount: Math.abs(n(r.weekly_refunds_amount_fact)),
})

// Локальный тестовый переключатель: в бою должен оставаться false/null.
const SIMULATE_TODAY_MID_PERIOD = false
const SIMULATE_TARGET_PERIOD: { year: number; month: number } | null = null
const resolveToday = (period: PlanningPeriod): string => {
  if (!SIMULATE_TODAY_MID_PERIOD) return getLocalIsoDate()
  const d = new Date(`${period.period_start}T00:00:00`)
  d.setDate(d.getDate() + Math.floor(Math.max(period.period_len_days - 1, 0) / 2))
  return toIso(d)
}

const buildWeeks = (period: PlanningPeriod, today: string): WeekDescriptor[] => {
  const start = new Date(`${period.period_start}T00:00:00`), end = new Date(`${period.period_end}T00:00:00`)
  const count = Math.ceil(period.period_len_days / 7)
  return Array.from({ length: count }, (_, i) => {
    const ws = new Date(start); ws.setDate(ws.getDate() + i * 7)
    const we = new Date(ws); we.setDate(we.getDate() + 6)
    if (we > end) we.setTime(end.getTime())
    const wsIso = toIso(ws), weIso = toIso(we)
    return {
      weekNumber: i + 1, week_start: wsIso, week_end: weIso,
      week_len_days: Math.floor((we.getTime() - ws.getTime()) / 86400000) + 1,
      formData: { ...EMPTY_FORM }, isStarted: today >= wsIso, isCompleted: today > weIso,
      hasSavedPlan: false, factRow: null,
      planMode: 'auto', planSource: null, manualUpdatedAt: null, manualUpdatedByUserId: null,
    }
  })
}
const clone = (w: WeekDescriptor[]): WeekDescriptor[] => w.map((x) => ({ ...x, formData: { ...x.formData } }))

// Год/месяц периода для запроса studio_weekly_plan — берём по середине периода (Чт→Ср на стыке месяцев).
const periodMidParts = (p: PlanningPeriod) => {
  const mid = new Date((new Date(p.period_start).getTime() + new Date(p.period_end).getTime()) / 2)
  return { y: mid.getFullYear(), m: mid.getMonth() + 1 }
}
const PERIOD_MONTH_NAMES = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
]
const formatPlanningPeriodLabel = (period: PlanningPeriod) => {
  const { y, m } = periodMidParts(period)
  return `${PERIOD_MONTH_NAMES[m - 1] ?? `Месяц ${m}`} ${y}`
}

type ScenarioKey = 'average' | 'reset' | null

// ── Конфиг строк сетки ───────────────────────────────────────────────────────
type Fmt = 'int' | 'money' | 'pct'
type Row =
  | { type: 'group'; label: string; collapsible?: boolean; expanded?: boolean }
  | { type: 'input'; label: string; field: keyof PlanFormData; fmt: Fmt; factKey?: keyof WeeklyFactRow }
  | { type: 'knob'; label: string; field: keyof PlanFormData; fmt: Fmt }
  | { type: 'derived'; label: string; key: keyof Derived; fmt: Fmt; highlight?: boolean; factKey?: keyof WeeklyFactRow }

interface VisibilityCtx {
  regularInline: boolean       // абонементы показываются инлайн (REGULAR | MIXED)
  regularCollapsible: boolean  // абонементы в сворачиваемом блоке (чистый SUBSCRIPTION)
  regularExpanded: boolean     // блок «Абонементы» раскрыт
  subscription: boolean        // subscriptionSectionsVisible
  subscriptionNew: boolean     // subscriptionNewSalesVisible
}

// Регулярные (абонементные) строки — переиспользуются инлайн и в блоке «Абонементы»
const REGULAR_NEW_SALES_ROWS: Row[] = [
  { type: 'knob', label: 'Конверсия в покупку абонемента', field: 'newSalesConversion', fmt: 'pct' },
  { type: 'derived', label: '→ Купят первый абонемент', key: 'newSalesCount', fmt: 'int', factKey: 'weekly_new_sales_fact' },
  { type: 'knob', label: 'Средний чек (новые)', field: 'avgNewSalesCheck', fmt: 'money' },
  { type: 'derived', label: 'Итого выручка от новых клиентов', key: 'newSalesAmountRegular', fmt: 'money', highlight: true, factKey: 'weekly_new_sales_amount_fact' },
]
const REGULAR_RETENTION_ROWS: Row[] = [
  { type: 'input', label: 'Будет продлений абонементов', field: 'renewalsCount', fmt: 'int', factKey: 'weekly_renewals_count_fact' },
  { type: 'knob', label: 'Средний чек продления', field: 'avgRenewalCheck', fmt: 'money' },
  { type: 'derived', label: '→ Выручка от продлений', key: 'renewalsAmount', fmt: 'money', factKey: 'weekly_renewals_amount_fact' },
  { type: 'input', label: 'Будет апгрейдов абонементов', field: 'upgradesCount', fmt: 'int', factKey: 'weekly_upgrades_count_fact' },
  { type: 'knob', label: 'Средний чек апгрейда', field: 'avgUpgradeCheck', fmt: 'money' },
  { type: 'derived', label: '→ Выручка от апгрейдов', key: 'upgradesAmount', fmt: 'money', factKey: 'weekly_upgrades_amount_fact' },
  { type: 'input', label: 'Будет продаж абонементов БЧС', field: 'returningClientsCount', fmt: 'int', factKey: 'weekly_returning_clients_count_fact' },
  { type: 'knob', label: 'Средний чек БЧС', field: 'avgReturningClientsCheck', fmt: 'money' },
  { type: 'derived', label: '→ Выручка от БЧС', key: 'returningClientsAmount', fmt: 'money', factKey: 'weekly_returning_clients_amount_fact' },
  { type: 'derived', label: 'Всего покупок не новыми', key: 'totalNonNewSales', fmt: 'int' },
  { type: 'derived', label: 'Средний чек не новых', key: 'avgNonNewCheck', fmt: 'money' },
  { type: 'derived', label: 'Итого выручка не новых (абонементы)', key: 'totalNonNewAmount', fmt: 'money', highlight: true },
]

const buildRows = (ctx: VisibilityCtx): Row[] => {
  const rows: Row[] = [
    { type: 'group', label: 'Маркетинг и лиды' },
    { type: 'input', label: 'Количество лидов', field: 'leadsCount', fmt: 'int', factKey: 'weekly_leads_fact' },
    { type: 'input', label: 'Плановый расход на маркетинг', field: 'marketingBudget', fmt: 'money', factKey: 'weekly_marketing_expenses_amount_fact' },
    { type: 'derived', label: 'Расчётная стоимость лида', key: 'leadPrice', fmt: 'money' },

    { type: 'group', label: 'Воронка пробного' },
    { type: 'knob', label: 'Конверсия в запись на пробное', field: 'appointmentConversion', fmt: 'pct' },
    { type: 'derived', label: '→ Сделаем записей на пробное', key: 'appointmentsCount', fmt: 'int', factKey: 'weekly_appointments_fact' },
    { type: 'knob', label: 'Конверсия в приход на пробное', field: 'trialVisitConversion', fmt: 'pct' },
    { type: 'derived', label: '→ Придёт на пробное', key: 'trialVisitsCount', fmt: 'int', factKey: 'weekly_new_visitors_fact' },
  ]

  if (ctx.regularInline) rows.push(...REGULAR_NEW_SALES_ROWS)
  if (ctx.subscriptionNew) {
    rows.push(
      { type: 'knob', label: 'Конверсия в первую подписку', field: 'newSubscriptionsConversion', fmt: 'pct' },
      { type: 'derived', label: '→ Купят первую подписку', key: 'newSubscriptionsCount', fmt: 'int', factKey: 'weekly_new_subscriptions_count_fact' },
      { type: 'knob', label: 'Средний чек первой подписки', field: 'avgNewSubscriptionCheck', fmt: 'money' },
      { type: 'knob', label: 'Средний членский взнос нового клиента', field: 'avgMembershipFeeNew', fmt: 'money' },
      { type: 'derived', label: 'Итого выручка от новых подписок', key: 'newSalesAmountSubscription', fmt: 'money', highlight: true, factKey: 'weekly_new_subscriptions_amount_fact' },
      { type: 'derived', label: 'Итого членские взносы новых клиентов', key: 'membershipFeesNewAmount', fmt: 'money', factKey: 'weekly_membership_fees_new_amount_fact' },
    )
  }

  if (ctx.regularInline) {
    rows.push({ type: 'group', label: 'Продления и апгрейды (абонементы)' }, ...REGULAR_RETENTION_ROWS)
  }

  if (ctx.subscription) {
    rows.push(
      { type: 'group', label: 'Подписки · Автопродления' },
      { type: 'input', label: 'Количество автопродлений', field: 'subscriptionRenewalsCount', fmt: 'int', factKey: 'weekly_subscription_renewals_count_fact' },
      { type: 'knob', label: 'Средний чек автопродления', field: 'avgSubscriptionRenewalCheck', fmt: 'money' },
      { type: 'derived', label: '→ Выручка от автопродлений', key: 'subscriptionRenewalsAmount', fmt: 'money', factKey: 'weekly_subscription_renewals_amount_fact' },
      { type: 'group', label: 'Подписки · Переходы с абонемента' },
      { type: 'input', label: 'Количество переходов на подписку', field: 'subscriptionUpgradesCount', fmt: 'int', factKey: 'weekly_subscription_upgrades_count_fact' },
      { type: 'knob', label: 'Средний чек перехода', field: 'avgSubscriptionUpgradeCheck', fmt: 'money' },
      { type: 'knob', label: 'Средний членский взнос при переходе', field: 'avgMembershipFeeSubscriptionUpgrade', fmt: 'money' },
      { type: 'derived', label: '→ Выручка от переходов', key: 'subscriptionUpgradesAmount', fmt: 'money', factKey: 'weekly_subscription_upgrades_amount_fact' },
      { type: 'derived', label: 'Итого ЧВ при переходах', key: 'membershipFeesSubscriptionUpgradesAmount', fmt: 'money', factKey: 'weekly_membership_fees_subscription_upgrades_amount_fact' },
      { type: 'group', label: 'Подписки · БЧС' },
      { type: 'input', label: 'Будет продаж подписок БЧС', field: 'subscriptionReturningClientsCount', fmt: 'int', factKey: 'weekly_subscription_returning_clients_count_fact' },
      { type: 'knob', label: 'Средний чек подписки БЧС', field: 'avgSubscriptionReturningClientsCheck', fmt: 'money' },
      { type: 'knob', label: 'Средний членский взнос БЧС', field: 'avgMembershipFeeSubscriptionReturning', fmt: 'money' },
      { type: 'derived', label: '→ Выручка от подписок БЧС', key: 'subscriptionReturningClientsAmount', fmt: 'money', factKey: 'weekly_subscription_returning_clients_amount_fact' },
      { type: 'derived', label: 'Итого ЧВ БЧС', key: 'membershipFeesSubscriptionReturningAmount', fmt: 'money', factKey: 'weekly_membership_fees_subscription_returning_amount_fact' },
      { type: 'derived', label: 'Всего покупок подписок не новыми', key: 'subscriptionNonNewSales', fmt: 'int' },
      { type: 'derived', label: 'Средний чек подписок не новых', key: 'avgSubscriptionNonNewCheck', fmt: 'money' },
      { type: 'derived', label: 'Итого выручка не новых (подписки)', key: 'subscriptionNonNewAmount', fmt: 'money', highlight: true },
    )
  }

  // Чистый SUBSCRIPTION: план по обычным абонементам — в сворачиваемом блоке
  if (ctx.regularCollapsible) {
    rows.push({ type: 'group', label: 'Абонементы — план по обычным абонементам', collapsible: true, expanded: ctx.regularExpanded })
    if (ctx.regularExpanded) {
      rows.push(
        ...REGULAR_NEW_SALES_ROWS,
        { type: 'group', label: 'Абонементы · Продления и апгрейды' },
        ...REGULAR_RETENTION_ROWS,
      )
    }
  }

  rows.push(
    { type: 'group', label: 'Дополнительные доходы' },
    { type: 'input', label: 'Продаж блоков индив./сплит', field: 'privateSessionsCount', fmt: 'int', factKey: 'weekly_private_sessions_count_fact' },
    { type: 'knob', label: 'Средний чек индив./сплит', field: 'avgPrivateSessionCheck', fmt: 'money' },
    { type: 'derived', label: '→ Выручка от индив./сплит', key: 'privateSessionsAmount', fmt: 'money', factKey: 'weekly_private_sessions_amount_fact' },
    { type: 'input', label: 'Прочая выручка', field: 'otherIncome', fmt: 'money', factKey: 'weekly_other_income_fact' },
    { type: 'input', label: 'План возвратов', field: 'refundsAmount', fmt: 'money' },
    { type: 'group', label: 'Итого' },
    { type: 'derived', label: 'ВЫРУЧКА ВСЕГО', key: 'totalRevenue', fmt: 'money', highlight: true },
  )
  return rows
}

const fmtBy = (fmt: Fmt, v: number, sym = '₽') => fmt === 'money' ? fmtMoney(v, sym) : fmt === 'pct' ? `${round2(v)}%` : fmtInt(v)

// Период-значение для knob-строк: подразумеваемое из агрегатов
const knobPeriodValue = (field: keyof PlanFormData, a: Aggregates): number => {
  switch (field) {
    case 'appointmentConversion': return pct(a.appointmentsCount, a.leadsCount)
    case 'trialVisitConversion': return pct(a.trialVisitsCount, a.appointmentsCount)
    case 'newSalesConversion': return pct(a.newSalesCount, a.trialVisitsCount)
    case 'newSubscriptionsConversion': return pct(a.newSubscriptionsCount, a.trialVisitsCount)
    case 'avgNewSalesCheck': return avg(a.newSalesAmountRegular, a.newSalesCount)
    case 'avgNewSubscriptionCheck': return avg(a.newSalesAmountSubscription, a.newSubscriptionsCount)
    case 'avgMembershipFeeNew': return avg(a.membershipFeesNewAmount, a.newSubscriptionsCount)
    case 'avgRenewalCheck': return avg(a.renewalsAmount, a.renewalsCount)
    case 'avgUpgradeCheck': return avg(a.upgradesAmount, a.upgradesCount)
    case 'avgReturningClientsCheck': return avg(a.returningClientsAmount, a.returningClientsCount)
    case 'avgSubscriptionRenewalCheck': return avg(a.subscriptionRenewalsAmount, a.subscriptionRenewalsCount)
    case 'avgSubscriptionUpgradeCheck': return avg(a.subscriptionUpgradesAmount, a.subscriptionUpgradesCount)
    case 'avgMembershipFeeSubscriptionUpgrade': return avg(a.membershipFeesSubscriptionUpgradesAmount, a.subscriptionUpgradesCount)
    case 'avgSubscriptionReturningClientsCheck': return avg(a.subscriptionReturningClientsAmount, a.subscriptionReturningClientsCount)
    case 'avgMembershipFeeSubscriptionReturning': return avg(a.membershipFeesSubscriptionReturningAmount, a.subscriptionReturningClientsCount)
    case 'avgPrivateSessionCheck': return avg(a.privateSessionsAmount, a.privateSessionsCount)
    default: return 0
  }
}

type Aggregates = { [K in keyof PlanFormData]: number } & { [K in keyof Derived]: number }

const DERIVED_KEYS = Object.keys(derive(EMPTY_FORM)) as (keyof Derived)[]
const FORM_KEYS = Object.keys(EMPTY_FORM) as (keyof PlanFormData)[]

// Агрегат набора недельных формдат: суммы вводимых + суммы производных
const aggregateForms = (forms: PlanFormData[]): Aggregates => {
  const r: any = {}
  FORM_KEYS.forEach((k) => { r[k] = forms.reduce((s, f) => s + f[k], 0) })
  const dv = forms.map(derive)
  DERIVED_KEYS.forEach((k) => { r[k] = dv.reduce((s, d) => s + d[k], 0) })
  return r as Aggregates
}

// Производные-ставки: в итоге не сумма, а подразумеваемое из агрегата
const DERIVED_RATE_TOTAL: Partial<Record<keyof Derived, (a: Aggregates) => number>> = {
  leadPrice: (a) => avg(a.marketingBudget, a.leadsCount),
  avgNonNewCheck: (a) => avg(a.totalNonNewAmount, a.totalNonNewSales),
  avgSubscriptionNonNewCheck: (a) => avg(a.subscriptionNonNewSubscriptionsAmount, a.subscriptionNonNewSales),
}

// Значение строки из агрегата: derived → сумма (или подразумеваемая ставка), knob → ставка, input → сумма
const totalForRow = (row: Extract<Row, { type: 'input' | 'knob' | 'derived' }>, a: Aggregates): number =>
  row.type === 'derived' ? (DERIVED_RATE_TOTAL[row.key]?.(a) ?? a[row.key]) : row.type === 'knob' ? knobPeriodValue(row.field, a) : a[row.field]

// Аддитивная строка (объём/выручка) — для неё осмыслен «Остаток плана» = план − факт.
// Ставки (knob, подразумеваемые derived-rate) суммировать/вычитать нельзя → остатка нет.
const isAdditiveRow = (row: Extract<Row, { type: 'input' | 'knob' | 'derived' }>): boolean =>
  row.type === 'input' ? true : row.type === 'derived' ? !(row.key in DERIVED_RATE_TOTAL) : false

interface TempoSource {
  factRow: WeeklyFactRow
  observedDays: number
}

const daysBetweenInclusive = (startIso: string, endIso: string): number => {
  const start = new Date(`${startIso}T00:00:00`)
  const end = new Date(`${endIso}T00:00:00`)
  return Math.floor((end.getTime() - start.getTime()) / 86400000) + 1
}

const observedDaysForWeek = (w: WeekDescriptor, today: string): number => {
  if (!w.isStarted) return 0
  if (w.isCompleted) return w.week_len_days
  const cappedToday = today > w.week_end ? w.week_end : today
  return Math.max(1, Math.min(w.week_len_days, daysBetweenInclusive(w.week_start, cappedToday)))
}

// Grace-окно ручного редактирования начавшейся недели (зеркало боевого getStartedWeekEditGraceDays):
// Н1 — 5 дней от старта, остальные — 2 дня. (В боевом «2 для остальных» — только focus-group франчайзи;
// в прототипе на одну студию гейт не различаем.) День старта = 0, редактируема пока день < grace.
const graceDaysForWeek = (weekNumber: number): number => (weekNumber === 1 ? 5 : 2)
const isGraceEditable = (w: WeekDescriptor, today: string): boolean =>
  w.isStarted && !w.isCompleted && daysBetweenInclusive(w.week_start, today) - 1 < graceDaysForWeek(w.weekNumber)
// Неделя редактируема вручную: будущая, либо начавшаяся в пределах grace-окна.
const isWeekEditable = (w: WeekDescriptor, today: string): boolean =>
  (!w.isStarted && !w.isCompleted) || isGraceEditable(w, today)

// Прогноз недели по темпу наблюдаемых дней: завершённые недели + текущая пропорционально её прошедшим дням.
// Объёмы scale по дням, ставки — подразумеваемые из факт-сумм.
const buildTempoForm = (sources: TempoSource[], days: number): PlanFormData | null => {
  if (sources.length === 0) return null
  const totalDays = sources.reduce((s, source) => s + source.observedDays, 0)
  const sf = (ff: keyof WeeklyFactRow) => sources.reduce((s, source) => s + Math.abs(n(source.factRow[ff])), 0)
  const t = {
    mk: sf('weekly_marketing_expenses_amount_fact'), leads: sf('weekly_leads_fact'), appt: sf('weekly_appointments_fact'),
    vis: sf('weekly_new_visitors_fact'), nsC: sf('weekly_new_sales_fact'), nsA: sf('weekly_new_sales_amount_fact'),
    mfNew: sf('weekly_membership_fees_new_amount_fact'), renC: sf('weekly_renewals_count_fact'), renA: sf('weekly_renewals_amount_fact'),
    upC: sf('weekly_upgrades_count_fact'), upA: sf('weekly_upgrades_amount_fact'), retC: sf('weekly_returning_clients_count_fact'),
    retA: sf('weekly_returning_clients_amount_fact'), nSubC: sf('weekly_new_subscriptions_count_fact'), nSubA: sf('weekly_new_subscriptions_amount_fact'),
    mfUpA: sf('weekly_membership_fees_subscription_upgrades_amount_fact'), mfRetA: sf('weekly_membership_fees_subscription_returning_amount_fact'),
    sRenC: sf('weekly_subscription_renewals_count_fact'), sRenA: sf('weekly_subscription_renewals_amount_fact'),
    sUpC: sf('weekly_subscription_upgrades_count_fact'), sUpA: sf('weekly_subscription_upgrades_amount_fact'),
    sRetC: sf('weekly_subscription_returning_clients_count_fact'), sRetA: sf('weekly_subscription_returning_clients_amount_fact'),
    psC: sf('weekly_private_sessions_count_fact'), psA: sf('weekly_private_sessions_amount_fact'),
    oi: sf('weekly_other_income_fact'), rf: sf('weekly_refunds_amount_fact'),
  }
  const scale = totalDays > 0 ? days / totalDays : 0
  return {
    marketingBudget: round2(t.mk * scale), leadsCount: round2(t.leads * scale),
    appointmentConversion: pct(t.appt, t.leads), trialVisitConversion: pct(t.vis, t.appt),
    newSalesConversion: pct(t.nsC, t.vis), newSubscriptionsConversion: pct(t.nSubC, t.vis),
    avgNewSalesCheck: avg(t.nsA, t.nsC), avgNewSubscriptionCheck: avg(t.nSubA, t.nSubC), avgMembershipFeeNew: avg(t.mfNew, t.nSubC),
    renewalsCount: round2(t.renC * scale), avgRenewalCheck: avg(t.renA, t.renC),
    upgradesCount: round2(t.upC * scale), avgUpgradeCheck: avg(t.upA, t.upC),
    returningClientsCount: round2(t.retC * scale), avgReturningClientsCheck: avg(t.retA, t.retC),
    subscriptionRenewalsCount: round2(t.sRenC * scale), avgSubscriptionRenewalCheck: avg(t.sRenA, t.sRenC),
    subscriptionUpgradesCount: round2(t.sUpC * scale), avgSubscriptionUpgradeCheck: avg(t.sUpA, t.sUpC),
    avgMembershipFeeSubscriptionUpgrade: avg(t.mfUpA, t.sUpC),
    subscriptionReturningClientsCount: round2(t.sRetC * scale), avgSubscriptionReturningClientsCheck: avg(t.sRetA, t.sRetC),
    avgMembershipFeeSubscriptionReturning: avg(t.mfRetA, t.sRetC),
    privateSessionsCount: round2(t.psC * scale), avgPrivateSessionCheck: avg(t.psA, t.psC),
    otherIncome: round2(t.oi * scale), refundsAmount: round2(t.rf * scale),
  }
}

const getRowHint = (row: Extract<Row, { type: 'input' | 'knob' | 'derived' }>, hints: PlanHints | null): HintValues | null => {
  if (!hints) return null
  const h = (plan: number | null | undefined, fact: number | null | undefined, fact3mo: number | null | undefined): HintValues =>
    ({ plan: plan ?? null, fact: fact ?? null, fact3mo: fact3mo ?? null })
  if (row.type === 'input') switch (row.field) {
    case 'leadsCount': return h(hints.plan_leads_week_avg, hints.fact_leads_week_avg, hints.fact_leads_3mo_avg)
    case 'marketingBudget': return h(hints.plan_marketing_budget_week_avg, hints.fact_marketing_budget_week_avg, hints.fact_marketing_budget_3mo_avg)
    case 'renewalsCount': return h(hints.plan_renewals_count_week_avg, hints.fact_renewals_count_week_avg, hints.fact_renewals_count_3mo_avg)
    case 'upgradesCount': return h(hints.plan_upgrades_count_week_avg, hints.fact_upgrades_count_week_avg, hints.fact_upgrades_count_3mo_avg)
    case 'returningClientsCount': return h(hints.plan_returning_clients_count_week_avg, hints.fact_returning_clients_count_week_avg, hints.fact_returning_clients_count_3mo_avg)
    case 'subscriptionRenewalsCount': return h(hints.plan_subscription_renewals_count_week_avg, hints.fact_subscription_renewals_count_week_avg, hints.fact_subscription_renewals_count_3mo_avg)
    case 'subscriptionUpgradesCount': return h(hints.plan_subscription_upgrades_count_week_avg, hints.fact_subscription_upgrades_count_week_avg, hints.fact_subscription_upgrades_count_3mo_avg)
    case 'subscriptionReturningClientsCount': return h(hints.plan_subscription_returning_clients_count_week_avg, hints.fact_subscription_returning_clients_count_week_avg, hints.fact_subscription_returning_clients_count_3mo_avg)
    case 'privateSessionsCount': return h(hints.plan_private_sessions_count_week_avg, hints.fact_private_sessions_count_week_avg, hints.fact_private_sessions_count_3mo_avg)
    default: return null
  }
  if (row.type === 'knob') switch (row.field) {
    case 'avgNewSalesCheck': return h(hints.plan_new_sales_aov_week_avg, hints.fact_new_sales_aov_week_avg, hints.fact_new_sales_aov_3mo_avg)
    case 'avgRenewalCheck': return h(hints.plan_renewals_aov_week_avg, hints.fact_renewals_aov_week_avg, hints.fact_renewals_aov_3mo_avg)
    case 'avgUpgradeCheck': return h(hints.plan_upgrades_aov_week_avg, hints.fact_upgrades_aov_week_avg, hints.fact_upgrades_aov_3mo_avg)
    case 'avgReturningClientsCheck': return h(hints.plan_returning_clients_aov_week_avg, hints.fact_returning_clients_aov_week_avg, hints.fact_returning_clients_aov_3mo_avg)
    case 'avgNewSubscriptionCheck': return h(hints.plan_new_subscriptions_aov_week_avg, hints.fact_new_subscriptions_aov_week_avg, hints.fact_new_subscriptions_aov_3mo_avg)
    case 'avgSubscriptionRenewalCheck': return h(hints.plan_subscription_renewals_aov_week_avg, hints.fact_subscription_renewals_aov_week_avg, hints.fact_subscription_renewals_aov_3mo_avg)
    case 'avgSubscriptionUpgradeCheck': return h(hints.plan_subscription_upgrades_aov_week_avg, hints.fact_subscription_upgrades_aov_week_avg, hints.fact_subscription_upgrades_aov_3mo_avg)
    case 'avgSubscriptionReturningClientsCheck': return h(hints.plan_subscription_returning_clients_aov_week_avg, hints.fact_subscription_returning_clients_aov_week_avg, hints.fact_subscription_returning_clients_aov_3mo_avg)
    case 'avgPrivateSessionCheck': return h(hints.plan_private_sessions_aov_week_avg, hints.fact_private_sessions_aov_week_avg, hints.fact_private_sessions_aov_3mo_avg)
    default: return null
  }
  if (row.type === 'derived') switch (row.key) {
    case 'appointmentsCount': return h(hints.plan_appointments_week_avg, hints.fact_appointments_week_avg, hints.fact_appointments_3mo_avg)
    case 'trialVisitsCount': return h(hints.plan_new_visitors_week_avg, hints.fact_new_visitors_week_avg, hints.fact_new_visitors_3mo_avg)
    case 'newSalesCount': return h(hints.plan_new_sales_count_week_avg, hints.fact_new_sales_count_week_avg, hints.fact_new_sales_count_3mo_avg)
    case 'newSalesAmountRegular': return h(hints.plan_new_sales_amount_week_avg, hints.fact_new_sales_amount_week_avg, hints.fact_new_sales_amount_3mo_avg)
    case 'renewalsAmount': return h(hints.plan_renewals_amount_week_avg, hints.fact_renewals_amount_week_avg, hints.fact_renewals_amount_3mo_avg)
    case 'upgradesAmount': return h(hints.plan_upgrades_amount_week_avg, hints.fact_upgrades_amount_week_avg, hints.fact_upgrades_amount_3mo_avg)
    case 'returningClientsAmount': return h(hints.plan_returning_clients_amount_week_avg, hints.fact_returning_clients_amount_week_avg, hints.fact_returning_clients_amount_3mo_avg)
    case 'newSubscriptionsCount': return h(hints.plan_new_subscriptions_count_week_avg, hints.fact_new_subscriptions_count_week_avg, hints.fact_new_subscriptions_count_3mo_avg)
    case 'newSalesAmountSubscription': return h(hints.plan_new_subscriptions_amount_week_avg, hints.fact_new_subscriptions_amount_week_avg, hints.fact_new_subscriptions_amount_3mo_avg)
    case 'membershipFeesNewAmount': return h(hints.plan_membership_fees_new_amount_week_avg, hints.fact_membership_fees_new_amount_week_avg, hints.fact_membership_fees_new_amount_3mo_avg)
    case 'subscriptionRenewalsAmount': return h(hints.plan_subscription_renewals_amount_week_avg, hints.fact_subscription_renewals_amount_week_avg, hints.fact_subscription_renewals_amount_3mo_avg)
    case 'subscriptionUpgradesAmount': return h(hints.plan_subscription_upgrades_amount_week_avg, hints.fact_subscription_upgrades_amount_week_avg, hints.fact_subscription_upgrades_amount_3mo_avg)
    case 'membershipFeesSubscriptionUpgradesAmount': return h(hints.plan_membership_fees_subscription_upgrades_amount_week_avg, hints.fact_membership_fees_subscription_upgrades_amount_week_avg, hints.fact_membership_fees_subscription_upgrades_amount_3mo_avg)
    case 'subscriptionReturningClientsAmount': return h(hints.plan_subscription_returning_clients_amount_week_avg, hints.fact_subscription_returning_clients_amount_week_avg, hints.fact_subscription_returning_clients_amount_3mo_avg)
    case 'membershipFeesSubscriptionReturningAmount': return h(hints.plan_membership_fees_subscription_returning_amount_week_avg, hints.fact_membership_fees_subscription_returning_amount_week_avg, hints.fact_membership_fees_subscription_returning_amount_3mo_avg)
    case 'privateSessionsAmount': return h(hints.plan_private_sessions_amount_week_avg, hints.fact_private_sessions_amount_week_avg, hints.fact_private_sessions_amount_3mo_avg)
    case 'totalRevenue': return h(hints.plan_gross_revenue_week_avg, hints.fact_gross_revenue_week_avg, hints.fact_gross_revenue_3mo_avg)
    default: return null
  }
  return null
}

// ── Компонент ─────────────────────────────────────────────────────────────────
export function WeeklyPlanGridPrototype() {
  const { studioId, partnerNumber } = useParams<{ studioId: string; partnerNumber?: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const studioIntId = parseInt(studioId || '0')
  const partnerNum = partnerNumber ? parseInt(partnerNumber) : null
  const requestedSelectedMonth = (() => {
    const state = location.state as { selectedMonth?: unknown; useSelectedMonth?: unknown } | null
    return state?.useSelectedMonth && typeof state.selectedMonth === 'string' ? state.selectedMonth : null
  })()

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [periodLabel, setPeriodLabel] = useState('')
  const [subType, setSubType] = useState<StudioSubscriptionType>('REGULAR')
  const [savedWeeks, setSavedWeeks] = useState<WeekDescriptor[]>([])
  const [weeks, setWeeks] = useState<WeekDescriptor[]>([])
  const [preview, setPreview] = useState<WeekDescriptor[] | null>(null)
  const [scenario, setScenario] = useState<ScenarioKey>(null)
  const [regularExpanded, setRegularExpanded] = useState(false)
  const [today, setToday] = useState(getLocalIsoDate())
  // Навигация по периодам: выбранный период отделён от календарного текущего периода.
  // «Сегодня» фиксируется один раз; переключение selectedPeriodId не двигает today — будущий период
  // показывается как «все недели впереди» (режим планирования), как в боевом.
  const [periods, setPeriods] = useState<PlanningPeriod[]>([])
  const [selectedPeriodId, setSelectedPeriodId] = useState<number | null>(null)
  const [navigationAnchorPeriodId, setNavigationAnchorPeriodId] = useState<number | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [isPrefillingPreviousPlan, setIsPrefillingPreviousPlan] = useState(false)
  const [periodLoading, setPeriodLoading] = useState(false) // P2-4: загрузка при смене периода
  const [showBulkModal, setShowBulkModal] = useState(false)  // P2-2: балк-планирование
  const [refreshTick, setRefreshTick] = useState(0)          // bump → перезагрузка периода после балка
  // Момент последнего ответа на confirm allocation-ячейки — глушит авто-рефокус,
  // из-за которого вопрос «открыть распределение?» зацикливался (см. handleAllocCellFocus).
  const allocFocusAnsweredAtRef = useRef(0)

  const { user } = useAuthStore()
  const { getUserRole, studios } = useStudioStore()
  const canEditPlan = useMemo(() => {
    const role = getUserRole(studioIntId)
    return Boolean(role && ['owner', 'manager', 'member'].includes(role))
  }, [getUserRole, studioIntId])

  const currentStudio = useMemo(() => studios.find((s) => s.studio_internal_id === studioIntId), [studios, studioIntId])
  const currencySym = useMemo(() => getCurrencySymbol(currentStudio?.currency_code), [currentStudio])
  const currencyCode = currentStudio?.currency_code ?? undefined

  // P2-5: студии франчайзи для переключателя и балка.
  const partnerStudios = useMemo(() => {
    if (partnerNum == null) return []
    return studios
      .filter((s) => Number(s.partner_number) === partnerNum)
      .sort((a, b) => {
        const cityDiff = (a.city?.city_name ?? '').localeCompare(b.city?.city_name ?? '', 'ru')
        if (cityDiff !== 0) return cityDiff
        return (a.studio_number ?? '').localeCompare(b.studio_number ?? '', 'ru', { numeric: true })
      })
  }, [studios, partnerNum])

  const [hints, setHints] = useState<PlanHints | null>(null)
  const [hintsLoading, setHintsLoading] = useState(false)
  const [clientsAtMonthStartFact, setClientsAtMonthStartFact] = useState<number | null>(null)

  // Эффект 1 (mount): список периодов, тип подписки, выбранный и календарный опорный периоды.
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const supabase = getSupabase()
        const realToday = getLocalIsoDate()
        const [periodsRes, subRes] = await Promise.all([
          supabase.from('metric_periods').select('period_id, period_start, period_end, period_len_days').order('period_start', { ascending: true }),
          supabase.from('studio_subscription_settings').select('subscription_type').eq('studio_internal_id', studioIntId).maybeSingle(),
        ])
        if (periodsRes.error) throw periodsRes.error
        const periodsList = (periodsRes.data ?? []) as PlanningPeriod[]
        const subscriptionType = ((subRes as any)?.data?.subscription_type as StudioSubscriptionType) || 'REGULAR'

        const calendarPeriod = periodsList.find((p) => p.period_start <= realToday && realToday <= p.period_end)
          ?? [...periodsList].reverse().find((p) => p.period_end < realToday)
          ?? periodsList[periodsList.length - 1]
        const simulatedCalendarPeriod = SIMULATE_TARGET_PERIOD
          ? periodsList.find((p) => {
              const { y, m } = periodMidParts(p)
              return y === SIMULATE_TARGET_PERIOD.year && m === SIMULATE_TARGET_PERIOD.month
            })
          : undefined
        const resolvedToday = resolveToday(simulatedCalendarPeriod ?? calendarPeriod)
        const { selectedPeriod, navigationAnchorPeriod } = resolveWeeklyPlanPeriods(
          periodsList,
          resolvedToday,
          requestedSelectedMonth,
        )
        if (!selectedPeriod || !navigationAnchorPeriod) throw new Error('Не найден период для студии')

        if (cancelled) return
        setPeriods(periodsList)
        setSubType(subscriptionType)
        setToday(resolvedToday)
        setSelectedPeriodId(selectedPeriod.period_id)
        setNavigationAnchorPeriodId(navigationAnchorPeriod.period_id)
      } catch (e: any) {
        if (cancelled) return
        console.error('[grid-prototype] periods load error', e)
        setError(e?.message ?? 'Ошибка загрузки'); setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [studioIntId, requestedSelectedMonth])

  // Эффект 2: план+факт выбранного периода. «Сегодня» фиксировано → недели будущего периода = впереди.
  useEffect(() => {
    if (selectedPeriodId == null || periods.length === 0) return
    const period = periods.find((p) => p.period_id === selectedPeriodId)
    if (!period) return
    setHints(null)
    setClientsAtMonthStartFact(null)
    setPeriodLoading(true)
    let cancelled = false
    ;(async () => {
      try {
        const supabase = getSupabase()
        const { y, m } = periodMidParts(period)
        let planQuery = supabase.from('studio_weekly_plan').select('*')
          .eq('studio_internal_id', studioIntId).eq('year_number', y).eq('month_number', m).order('week_number_in_period')
        if (partnerNum) planQuery = planQuery.eq('partner_number', partnerNum)

        // Дата последнего дня перед периодом (для базы клиентов)
        const prevDay = new Date(`${period.period_start}T00:00:00`)
        prevDay.setDate(prevDay.getDate() - 1)
        const prevDayIso = toIso(prevDay)

        const [planRowsRes, factRowsRes, clientsRes] = await Promise.all([
          planQuery,
          (supabase as any).from('studio_weekly_plan_vs_fact').select(FACT_SELECT)
            .eq('studio_internal_id', studioIntId).eq('period_id', period.period_id).order('week_number_in_period'),
          supabase.from('studio_daily_totals').select('total_clients_count')
            .eq('studio_internal_id', studioIntId).lte('metric_date', prevDayIso)
            .order('metric_date', { ascending: false }).limit(1).maybeSingle(),
        ])
        if (planRowsRes.error) throw planRowsRes.error

        const planByWeek = new Map<number, any>((planRowsRes.data ?? []).map((r: any) => [r.week_number_in_period, r]))
        const factByWeek = new Map<number, WeeklyFactRow>(((factRowsRes.data ?? []) as WeeklyFactRow[]).map((r) => [r.week_number_in_period, r]))
        const base = buildWeeks(period, today).map((w) => {
          const planRow = planByWeek.get(w.weekNumber)
          return {
            ...w, formData: mapPlanRow(planRow),
            hasSavedPlan: Boolean(planRow), factRow: factByWeek.get(w.weekNumber) ?? null,
            ...mapProvenance(planRow),
          }
        })

        if (cancelled) return
        setPeriodLabel(formatPlanningPeriodLabel(period))
        setSavedWeeks(clone(base))
        setWeeks(base)
        setPreview(null); setScenario(null) // сбрасываем сценарий при смене периода
        const clientsData = clientsRes.data as { total_clients_count?: number | null } | null
        const clientsAtMonthStart = clientsData?.total_clients_count ?? null
        if (!clientsRes.error && clientsAtMonthStart != null)
          setClientsAtMonthStartFact(clientsAtMonthStart)
        setLoading(false); setPeriodLoading(false)
      } catch (e: any) {
        if (cancelled) return
        console.error('[grid-prototype] period data load error', e)
        setError(e?.message ?? 'Ошибка загрузки'); setLoading(false); setPeriodLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [studioIntId, partnerNum, selectedPeriodId, periods, today, refreshTick])

  const selectedIndex = useMemo(() => periods.findIndex((p) => p.period_id === selectedPeriodId), [periods, selectedPeriodId])
  const selectedPeriod = useMemo(() => periods.find((p) => p.period_id === selectedPeriodId) ?? null, [periods, selectedPeriodId])
  const navigationAnchorPeriod = useMemo(
    () => periods.find((p) => p.period_id === navigationAnchorPeriodId) ?? null,
    [periods, navigationAnchorPeriodId],
  )

  // P2-3: следующий период доступен только с 4-й недели календарного текущего периода.
  // Назад — без ограничений; явно выбранный старый период не меняет календарную границу вперёд.
  const maxSelectableIndex = useMemo(() => {
    return getMaxSelectablePeriodIndex(periods, navigationAnchorPeriod, today)
  }, [navigationAnchorPeriod, periods, today])
  const canGoForward = selectedIndex >= 0 && selectedIndex < periods.length - 1 && selectedIndex + 1 <= maxSelectableIndex
  const canGoBack = selectedIndex > 0

  const isDirty = useMemo(
    () => weeks.some((w) => {
      const s = savedWeeks.find((sw) => sw.weekNumber === w.weekNumber)
      if (!s) return true
      if (w.planMode !== s.planMode || w.planSource !== s.planSource) return true
      return (Object.keys(EMPTY_FORM) as (keyof PlanFormData)[]).some((k) => w.formData[k] !== s.formData[k])
    }),
    [weeks, savedWeeks]
  )

  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => { if (isDirty) { e.preventDefault(); e.returnValue = '' } }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [isDirty])

  useEffect(() => {
    setHasUnsavedMetricsChanges(isDirty)
    return () => setHasUnsavedMetricsChanges(false)
  }, [isDirty])

  const goPeriod = (delta: number) => {
    if (delta > 0 && !canGoForward) return
    if (delta < 0 && !canGoBack) return
    if (isDirty && !window.confirm('Есть несохранённые изменения. Уйти без сохранения?')) return
    const next = periods[selectedIndex + delta]
    if (next) setSelectedPeriodId(next.period_id)
  }

  const display = preview ?? weeks

  // Режим экрана: «планирование» (период не начат — нет ни завершённых, ни идущих недель) vs
  // «корректировка» (период идёт). В планировании факт/остаток/прогноз = 0/дубль → их колонки и
  // блок сценариев скрываем, оставляя только ввод по неделям и «Итого план».
  const periodStarted = useMemo(() => weeks.some((w) => w.isStarted || w.isCompleted), [weeks])

  // Логика видимости — зеркало боевого экрана
  const presence = getChannelPresence(
    weeks.flatMap((w) => [w.formData.newSalesConversion, w.formData.avgNewSalesCheck, w.formData.renewalsCount, w.formData.avgRenewalCheck, w.formData.upgradesCount, w.formData.avgUpgradeCheck, w.formData.returningClientsCount, w.formData.avgReturningClientsCheck]),
    weeks.flatMap((w) => [w.formData.newSubscriptionsConversion, w.formData.avgNewSubscriptionCheck, w.formData.subscriptionRenewalsCount, w.formData.avgSubscriptionRenewalCheck, w.formData.subscriptionUpgradesCount, w.formData.avgSubscriptionUpgradeCheck, w.formData.subscriptionReturningClientsCount, w.formData.avgSubscriptionReturningClientsCheck])
  )
  const showRegular = subType === 'REGULAR' || subType === 'MIXED'
  const showSubscription = subType === 'SUBSCRIPTION' || subType === 'MIXED'
  const shouldShowRegularPlanToggle = subType === 'SUBSCRIPTION'
  const vis: VisibilityCtx = {
    regularInline: showRegular,
    regularCollapsible: shouldShowRegularPlanToggle,
    regularExpanded,
    subscription: showSubscription || presence.hasSubscription,
    subscriptionNew: showSubscription || presence.hasSubscription,
  }
  const rows = useMemo(() => buildRows(vis), [vis.regularInline, vis.regularCollapsible, vis.regularExpanded, vis.subscription, vis.subscriptionNew])

  // Мини-гид по экрану: автозапуск один раз, когда сетка отрисована, + кнопка перезапуска.
  const tour = useOnboardingTour('weekly-plan-grid', !loading && !error && rows.length > 0)

  // Дефолт: блок «Абонементы» раскрыт, если у SUBSCRIPTION-студии есть регулярные данные (как hasRegularData в боевом)
  useEffect(() => {
    if (shouldShowRegularPlanToggle && presence.hasRegular) setRegularExpanded(true)
  }, [shouldShowRegularPlanToggle, presence.hasRegular])

  const targetWeekNumbers = useMemo(
    () => new Set(weeks.filter((w) => !w.isStarted && w.weekNumber !== 1).map((w) => w.weekNumber)),
    [weeks]
  )

  // P2-2: оставшиеся редактируемые недели (для балка) — будущие + текущая в grace; завершённые не трогаем.
  const editableWeekNumbers = useMemo(
    () => weeks.filter((w) => isWeekEditable(w, today)).map((w) => w.weekNumber),
    [weeks, today]
  )
  const canShowBulkPlanButton = canEditPlan && partnerStudios.length > 1 && partnerNum != null && editableWeekNumbers.length > 0

  const tempoSources = useMemo<TempoSource[]>(() => weeks
    .filter((w) => w.factRow && (w.isCompleted || w.isStarted))
    .map((w) => ({ factRow: w.factRow!, observedDays: observedDaysForWeek(w, today) }))
    .filter((source) => source.observedDays > 0), [weeks, today])

  // Факт по неделе (синтетический формдата + его derive) — для завершённых/идущих недель
  const weekFact = useMemo(() => {
    const m = new Map<number, { form: PlanFormData; derived: Derived }>()
    weeks.forEach((w) => { if (w.factRow) { const form = factToFormData(w.factRow); m.set(w.weekNumber, { form, derived: derive(form) }) } })
    return m
  }, [weeks])

  // Агрегаты периода (план)
  const agg = useMemo<Aggregates>(() => aggregateForms(display.map((w) => w.formData)), [display])
  const savedAgg = useMemo<Aggregates>(() => aggregateForms(savedWeeks.map((w) => w.formData)), [savedWeeks])
  const scenarioAgg = useMemo<Aggregates>(() => aggregateForms(display.map((w) => {
    if (scenario === 'average' && (w.isCompleted || w.isStarted)) return weekFact.get(w.weekNumber)?.form ?? w.formData
    return w.formData
  })), [display, scenario, weekFact])
  const activePlanAgg = scenario === 'average' ? scenarioAgg : agg

  // Прогнозные формдаты по неделям:
  // завершённые → факт; текущая → её собственный факт, масштабированный на неделю; будущие → общий темп.
  const forecastForms = useMemo(() => display.map((w) => {
    if (w.isCompleted) return weekFact.get(w.weekNumber)?.form ?? w.formData
    if (w.isStarted && w.factRow) {
      return buildTempoForm(
        [{ factRow: w.factRow, observedDays: observedDaysForWeek(w, today) }],
        w.week_len_days
      ) ?? weekFact.get(w.weekNumber)?.form ?? w.formData
    }
    return buildTempoForm(tempoSources, w.week_len_days) ?? w.formData
  }), [display, tempoSources, weekFact, today])

  // Факт на сегодня: завершённые и текущая (частично) — факт; будущие — нули
  const factTodayForms = useMemo(() => display.map((w) =>
    (w.isCompleted || w.isStarted) ? (weekFact.get(w.weekNumber)?.form ?? { ...EMPTY_FORM }) : { ...EMPTY_FORM }
  ), [display, weekFact])

  const factTodayAgg = useMemo(() => aggregateForms(factTodayForms), [factTodayForms])
  const forecastAgg = useMemo(() => aggregateForms(forecastForms), [forecastForms])

  const savedPeriodRevenue = useMemo(() => savedWeeks.reduce((s, w) => s + derive(w.formData).totalRevenue, 0), [savedWeeks])
  const hasSavedPeriodPlan = useMemo(() => savedWeeks.some((w) => w.hasSavedPlan), [savedWeeks])
  const hasPlanChangePreview = isDirty || preview !== null
  const periodPlanSummaryLabel = !hasSavedPeriodPlan
    ? 'Черновик плана периода'
    : hasPlanChangePreview
      ? 'Новый план периода'
      : 'План периода'
  const remainingToCurrentPlan = activePlanAgg.totalRevenue - factTodayAgg.totalRevenue
  const canCompareForecastToPlan = activePlanAgg.totalRevenue > 0

  // ── Редактирование ────────────────────────────────────────────────────────
  // Провенанс «правлено руками» — для реально изменённой/скопированной недели (P0-1a).
  const manualStamp = (): Provenance => ({
    planMode: 'manual', planSource: 'manual',
    manualUpdatedAt: new Date().toISOString(), manualUpdatedByUserId: user?.id ?? null,
  })
  // P2-2 #4: лиды/бюджет завязаны на распределение по студиям (%). Их ручная правка отвязывает
  // неделю (→ manual, бейдж гаснет). Правка прочих полей на allocation-неделе источник сохраняет.
  const isAllocField = (field: keyof PlanFormData) => field === 'leadsCount' || field === 'marketingBudget'
  const editCell = (weekNumber: number, field: keyof PlanFormData, value: number) => {
    setWeeks((prev) => prev.map((w) => {
      if (w.weekNumber !== weekNumber) return w
      const keepAlloc = w.planSource === 'partner_allocation' && !isAllocField(field)
      return { ...w, formData: { ...w.formData, [field]: value }, ...(keepAlloc ? {} : manualStamp()) }
    }))
  }

  // P2-2 #4: открыть распределение вместо ручной правки allocation-ячейки (с защитой грязной сетки).
  const openBulkModal = () => {
    if (isDirty) { window.alert('Сначала сохраните или отмените изменения сетки, затем откройте распределение.'); return }
    setShowBulkModal(true)
  }
  const handleAllocCellFocus = () => {
    if (partnerStudios.length <= 1) return
    // Нативный confirm на onFocus зацикливался: при закрытии диалога фокус
    // возвращается в тот же input → onFocus срабатывает снова → confirm заново
    // (выйти можно было только «ОК», который уводит фокус в модалку). Глушим этот
    // авто-рефокус коротким окном после ответа, чтобы «Отмена» оставляла поле
    // доступным для ручной правки, а не открывала вопрос по кругу.
    if (Date.now() - allocFocusAnsweredAtRef.current < 600) return
    const openDistribution = window.confirm('Лиды и маркетинг на этой неделе заданы распределением по студиям. Ручная правка отвяжет неделю от общих долей %.\n\nОткрыть распределение? (Отмена — править вручную)')
    allocFocusAnsweredAtRef.current = Date.now()
    if (openDistribution) {
      ;(document.activeElement as HTMLElement | null)?.blur?.()
      openBulkModal()
    }
  }

  const prefillFromPreviousPeriodPlan = async () => {
    if (!canEditPlan || isPrefillingPreviousPlan) return
    if (!partnerNum) { alert('Ошибка: не удалось определить франчайзи'); return }
    if (selectedIndex <= 0) { alert('Нет доступного предыдущего периода'); return }

    const targetWeeks = weeks.filter((w) => isWeekEditable(w, today))
    if (targetWeeks.length === 0) {
      alert('В выбранном периоде нет редактируемых недель')
      return
    }

    const confirmed = window.confirm(
      `Эта операция загрузит данные плана предыдущего периода и перезапишет редактируемые недели (${fmtWeekNumbersText(targetWeeks.map((w) => w.weekNumber))}). Продолжить?`
    )
    if (!confirmed) return

    setIsPrefillingPreviousPlan(true)
    try {
      const supabase = getSupabase()
      const prevPeriod = periods[selectedIndex - 1]
      const { y, m } = periodMidParts(prevPeriod)
      const { data, error } = await (supabase as any)
        .from('studio_weekly_plan')
        .select('*')
        .eq('studio_internal_id', studioIntId)
        .eq('year_number', y)
        .eq('month_number', m)
        .eq('partner_number', partnerNum)
        .order('week_number_in_period')

      if (error) throw error
      const prevRows = (data ?? []) as any[]
      if (prevRows.length === 0) {
        alert('План за предыдущий период не найден')
        return
      }

      const sortedPrevRows = [...prevRows].sort((a, b) => Number(a.week_number_in_period) - Number(b.week_number_in_period))
      // Берём Н1 прошлого периода как базовый шаблон. Поздние недели могли быть догоном,
      // сценарием или ручным ускорением плана, поэтому их не переносим в новый период.
      const sourceRow = sortedPrevRows.find((row) => Number(row.week_number_in_period) === 1) ?? sortedPrevRows[0]
      const editableNumbers = new Set(targetWeeks.map((w) => w.weekNumber))
      setWeeks((prev) => prev.map((w) => {
        if (!editableNumbers.has(w.weekNumber)) return w
        const previousPlanForm = mapPlanRow(sourceRow)
        return {
          ...w,
          formData: {
            ...previousPlanForm,
            marketingBudget: w.formData.marketingBudget,
            leadsCount: w.formData.leadsCount,
          },
          ...manualStamp(),
        }
      }))
    } catch (e: any) {
      alert('Ошибка при загрузке плана предыдущего периода: ' + (e?.message ?? String(e)))
    } finally {
      setIsPrefillingPreviousPlan(false)
    }
  }

  // Копирование данных недели 1 на все остальные редактируемые недели (источник-шаблон).
  const week1 = weeks.find((w) => w.weekNumber === 1)
  const week1Editable = Boolean(week1 && isWeekEditable(week1, today))
  const copyableTargets = useMemo(() => weeks.filter((w) => w.weekNumber !== 1 && isWeekEditable(w, today)), [weeks, today])
  const canCopyWeek1 = canEditPlan && week1Editable && copyableTargets.length > 0
  const copyAllWeek1ToAll = () => {
    if (!canCopyWeek1 || !week1) return
    const targets = new Set(copyableTargets.map((w) => w.weekNumber))
    setWeeks((prev) => prev.map((w) => targets.has(w.weekNumber) ? { ...w, formData: { ...week1.formData }, ...manualStamp() } : w))
  }
  const copyFieldWeek1ToAll = (field: keyof PlanFormData) => {
    if (!canCopyWeek1 || !week1) return
    const value = week1.formData[field]
    const targets = new Set(copyableTargets.map((w) => w.weekNumber))
    setWeeks((prev) => prev.map((w) => {
      if (!targets.has(w.weekNumber)) return w
      const keepAlloc = w.planSource === 'partner_allocation' && !isAllocField(field)
      return { ...w, formData: { ...w.formData, [field]: value }, ...(keepAlloc ? {} : manualStamp()) }
    }))
  }

  // ── Сценарии ──────────────────────────────────────────────────────────────
  const stageAverage = () => {
    const targets = weeks.filter((w) => targetWeekNumbers.has(w.weekNumber))
    if (tempoSources.length === 0 || targets.length === 0) return
    const next = clone(weeks)
    targets.forEach((tw) => {
      const idx = next.findIndex((w) => w.weekNumber === tw.weekNumber)
      const tempo = buildTempoForm(tempoSources, tw.week_len_days)
      if (tempo) {
        next[idx].formData = tempo
        // Сценарий «средний факт» — не ручная правка: avg_fact, manual_updated_* = null (зеркало боевого).
        next[idx].planMode = 'auto'; next[idx].planSource = 'avg_fact'
        next[idx].manualUpdatedAt = null; next[idx].manualUpdatedByUserId = null
      }
    })
    setPreview(next); setScenario('average')
  }

  const stageReset = () => {
    const next = clone(weeks)
    savedWeeks.forEach((sw) => {
      if (targetWeekNumbers.has(sw.weekNumber)) {
        const idx = next.findIndex((w) => w.weekNumber === sw.weekNumber)
        next[idx].formData = { ...sw.formData }
        // Сброс к сохранённому плану возвращает и провенанс той недели.
        next[idx].planMode = sw.planMode; next[idx].planSource = sw.planSource
        next[idx].manualUpdatedAt = sw.manualUpdatedAt; next[idx].manualUpdatedByUserId = sw.manualUpdatedByUserId
      }
    })
    setPreview(next); setScenario('reset')
  }

  const applyScenario = () => { if (preview) { setWeeks(preview); setPreview(null); setScenario(null) } }
  const cancelScenario = () => { setPreview(null); setScenario(null) }

  const resetUnsavedChanges = () => {
    if (!isDirty) return
    if (!window.confirm('Сбросить несохранённые изменения и вернуться к сохранённому плану?')) return
    setWeeks(clone(savedWeeks))
    setPreview(null)
    setScenario(null)
  }

  // ── P0-1: Сохранение в БД ─────────────────────────────────────────────────
  const handleSave = async () => {
    if (!canEditPlan || isSaving) return
    if (!partnerNum) { alert('Ошибка: не удалось определить франчайзи'); return }
    const period = periods.find((p) => p.period_id === selectedPeriodId)
    if (!period) return
    setIsSaving(true)
    try {
      const supabase = getSupabase()
      const { y: year_number, m: month_number } = periodMidParts(period)
      const now = new Date().toISOString()
      const rowsToSave = weeks.map((w) => {
        const d = derive(w.formData)
        return {
          studio_internal_id: studioIntId,
          year_number,
          month_number,
          partner_number: partnerNum,
          week_number_in_period: w.weekNumber,
          decomposed_clients_plan: clientsAtMonthStartFact ?? null,
          cost_per_lead_plan: d.leadPrice,
          marketing_budget_plan: w.formData.marketingBudget,
          leads_count_plan: w.formData.leadsCount,
          booking_conversion_plan: w.formData.appointmentConversion,
          appointments_count_plan: d.appointmentsCount,
          visit_conversion_plan: w.formData.trialVisitConversion,
          new_visitors_count_plan: d.trialVisitsCount,
          sales_conversion_plan: w.formData.newSalesConversion,
          new_sales_count_plan: d.newSalesCount,
          new_sales_aov_plan: w.formData.avgNewSalesCheck,
          new_sales_amount_plan: d.newSalesAmountRegular,
          reneval_count_plan: w.formData.renewalsCount,
          reneval_aov_plan: w.formData.avgRenewalCheck,
          renevals_amount_plan: d.renewalsAmount,
          upgrades_count_plan: w.formData.upgradesCount,
          upgrades_aov_plan: w.formData.avgUpgradeCheck,
          upgrades_amount_plan: d.upgradesAmount,
          returning_clients_count_plan: w.formData.returningClientsCount,
          returning_clients_aov_plan: w.formData.avgReturningClientsCheck,
          returning_clients_amount_plan: d.returningClientsAmount,
          total_returning_sales_count_plan: d.totalNonNewSales,
          total_returning_sales_amount_plan: d.totalNonNewAmount,
          returning_sales_aov_plan: d.avgNonNewCheck,
          private_sessions_count_plan: w.formData.privateSessionsCount,
          private_sessions_aov_plan: w.formData.avgPrivateSessionCheck,
          private_sessions_amount_plan: d.privateSessionsAmount,
          other_income_plan: w.formData.otherIncome,
          refunds_amount_plan: d.refundsPlanAmount,
          gross_revenue_plan: d.totalRevenue,
          new_subscriptions_conversion_plan: w.formData.newSubscriptionsConversion,
          new_subscriptions_count_plan: d.newSubscriptionsCount,
          new_subscriptions_aov_plan: w.formData.avgNewSubscriptionCheck,
          new_subscriptions_amount_plan: d.newSalesAmountSubscription,
          membership_fees_new_plan: d.membershipFeesNewAmount,
          membership_fees_subscription_upgrades_plan: d.membershipFeesSubscriptionUpgradesAmount,
          membership_fees_subscription_returning_plan: d.membershipFeesSubscriptionReturningAmount,
          subscription_renewals_count_plan: w.formData.subscriptionRenewalsCount,
          subscription_renewals_aov_plan: w.formData.avgSubscriptionRenewalCheck,
          subscription_renewals_amount_plan: d.subscriptionRenewalsAmount,
          subscription_upgrades_count_plan: w.formData.subscriptionUpgradesCount,
          subscription_upgrades_aov_plan: w.formData.avgSubscriptionUpgradeCheck,
          subscription_upgrades_amount_plan: d.subscriptionUpgradesAmount,
          subscription_returning_clients_count_plan: w.formData.subscriptionReturningClientsCount,
          subscription_returning_clients_aov_plan: w.formData.avgSubscriptionReturningClientsCheck,
          subscription_returning_clients_amount_plan: d.subscriptionReturningClientsAmount,
          // Провенанс — по-недельно (P0-1a): manual_updated_* только для реально manual-недель,
          // иначе несём прежний источник и null (зеркало боевого handleSave:3069).
          plan_mode: w.planMode,
          plan_source: w.planSource,
          manual_updated_at: w.planMode === 'manual' ? (w.manualUpdatedAt ?? now) : null,
          manual_updated_by_user_id: w.planMode === 'manual' ? (w.manualUpdatedByUserId ?? user?.id ?? null) : null,
          updated_at: now,
        }
      })
      const { error } = await (supabase as any)
        .from('studio_weekly_plan')
        .upsert(rowsToSave, { onConflict: 'studio_internal_id,year_number,month_number,week_number_in_period,partner_number' })
      if (error) throw error
      const savedState = weeks.map((w) => ({ ...w, hasSavedPlan: true }))
      setSavedWeeks(clone(savedState))
      setWeeks(savedState)
    } catch (e: any) {
      alert('Ошибка сохранения: ' + (e?.message ?? String(e)))
    } finally {
      setIsSaving(false)
    }
  }

  const loadHints = async () => {
    if (hintsLoading) return
    const period = periods.find((p) => p.period_id === selectedPeriodId)
    if (!period) return
    setHintsLoading(true)
    try {
      const supabase = getSupabase()
      const { data, error: rpcError } = await (supabase as any).rpc('get_studio_plan_hints_v2', {
        p_studio_internal_id: studioIntId,
        p_target_period_start: period.period_start,
      })
      if (rpcError) throw rpcError
      setHints((data?.[0] ?? null) as PlanHints | null)
    } catch (e: any) {
      alert('Ошибка загрузки подсказок: ' + (e?.message ?? String(e)))
    } finally {
      setHintsLoading(false)
    }
  }

  const confirmLeaveDirty = (message: string) => !isDirty || window.confirm(message)
  const navigateToHistory = () => {
    if (!confirmLeaveDirty('Есть несохранённые изменения. Открыть историю без сохранения?')) return
    if (!partnerNum) { navigate('/partners'); return }
    navigate(`/partners/${partnerNum}/studios/${studioIntId}/plan-history`)
  }

  const navigateToPlanFact = () => {
    if (!confirmLeaveDirty('Есть несохранённые изменения. Открыть план-факт без сохранения?')) return
    if (!partnerNum) { navigate('/partners'); return }
    navigate(`/partners/${partnerNum}/studios/${studioIntId}/history`)
  }

  if (loading) return <div className="p-8 text-center text-muted-foreground">Загрузка плана…</div>
  if (error) return <div className="p-8 text-center text-red-600">Ошибка: {error}</div>

  const weekStatus = (w: WeekDescriptor) =>
    w.isCompleted ? { label: 'факт', cls: 'bg-emerald-50 text-emerald-700 ring-emerald-200' }
      : w.isStarted ? { label: 'идёт', cls: 'bg-amber-50 text-amber-700 ring-amber-200' }
        : { label: 'план', cls: 'bg-slate-50 text-slate-600 ring-slate-200' }

  return (
    <div className="min-h-screen bg-background pb-32">
      <div className="mx-auto max-w-[1500px] px-4 pt-3">
        <div className="mb-3 flex items-center justify-between gap-3">
          <button onClick={() => {
            if (isDirty && !window.confirm('Есть несохранённые изменения. Уйти без сохранения?')) return
            navigate(-1)
          }} className="flex items-center gap-2 rounded-lg px-2 py-1 text-sm hover:bg-muted">
            <ArrowLeft className="h-4 w-4" /> Назад
          </button>
          <div className="text-center" data-tour="period">
            <div className="flex items-center justify-center gap-2">
              <button onClick={() => goPeriod(-1)} disabled={!canGoBack}
                className="rounded-lg p-1 hover:bg-muted disabled:cursor-not-allowed disabled:opacity-30" title="Предыдущий период">
                <ChevronLeft className="h-4 w-4" />
              </button>
              <div className="flex items-center gap-1.5 text-base font-semibold tracking-tight">
                План периода — {periodLabel}
                {periodLoading && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
              </div>
              <button onClick={() => goPeriod(1)} disabled={!canGoForward}
                className="rounded-lg p-1 hover:bg-muted disabled:cursor-not-allowed disabled:opacity-30"
                title={canGoForward ? 'Следующий период' : 'Следующий период откроется с 4-й недели текущего периода'}>
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
            {SIMULATE_TODAY_MID_PERIOD && (
              <div className="flex items-center justify-center gap-1.5 text-xs text-amber-600">
                тест{SIMULATE_TARGET_PERIOD ? ` ${SIMULATE_TARGET_PERIOD.month}.${SIMULATE_TARGET_PERIOD.year}` : ''}: «сегодня» {fmtShortDate(today)}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {!canEditPlan && (
              <span className="rounded-full bg-rose-100 px-3 py-1 text-xs font-medium text-rose-700">Только просмотр</span>
            )}
            <button
              type="button"
              data-tour="help"
              onClick={tour.start}
              className="inline-flex h-7 w-7 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              title="Подсказка по экрану"
              aria-label="Показать подсказку по экрану"
            >
              <HelpCircle className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={navigateToHistory}
              className="inline-flex items-center gap-1.5 rounded-lg bg-primary/10 px-3 py-1.5 text-xs font-medium text-primary hover:bg-primary/20"
            >
              <History className="h-3.5 w-3.5" />
              История
            </button>
            <button
              type="button"
              onClick={navigateToPlanFact}
              className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted"
            >
              <BarChart3 className="h-3.5 w-3.5" />
              План-факт
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 xl:grid-cols-[1fr_320px]">
          {/* ── Сетка ── */}
          <Card className="overflow-hidden p-0" data-tour="grid">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/40">
                    <th className="sticky left-0 z-10 bg-muted/40 px-3 py-2 text-left font-medium text-muted-foreground" style={{ minWidth: 240 }}>Метрика</th>
                    {display.map((w) => {
                      const st = weekStatus(w)
                      const isTarget = scenario && targetWeekNumbers.has(w.weekNumber)
                      const isTemplate = !periodStarted && w.weekNumber === 1
                      const showCopyAll = canCopyWeek1 && w.weekNumber === 1
                      return (
                        <th key={w.weekNumber}
                          data-tour={isTemplate ? 'template' : undefined}
                          className={`px-2 py-2 text-center font-medium ${isTemplate ? 'bg-primary/5 ring-1 ring-inset ring-primary/30' : isTarget ? 'bg-sky-50' : ''}`}
                          style={{ minWidth: 96 }}>
                          <div className="flex items-center justify-center gap-1"><span>Н{w.weekNumber}</span>{(w.isCompleted || w.isStarted) && !isGraceEditable(w, today) && <Lock className="h-3 w-3 text-muted-foreground" />}</div>
                          <div className="text-[10px] font-normal text-muted-foreground">{fmtShortDate(w.week_start)}–{fmtShortDate(w.week_end)}</div>
                          {isTemplate
                            ? <span className="mt-0.5 inline-block rounded-full bg-primary/10 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-primary ring-1 ring-primary/30">шаблон</span>
                            : <span className={`mt-0.5 inline-block rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase ring-1 ${st.cls}`}>{st.label}</span>}
                          {showCopyAll && (
                            <button onClick={copyAllWeek1ToAll} title="Скопировать все данные недели 1 на остальные недели"
                              className="mt-1 flex w-full items-center justify-center gap-0.5 rounded-md border border-primary/30 bg-primary/5 px-1 py-0.5 text-[9px] font-medium text-primary hover:bg-primary/10">
                              <ChevronsRight className="h-3 w-3" /> на все
                            </button>
                          )}
                        </th>
                      )
                    })}
                    {periodStarted && <th className="border-l border-border px-3 py-2 text-center font-medium text-emerald-700" style={{ minWidth: 100 }}>Факт сегодня</th>}
                    {periodStarted && <th className="px-3 py-2 text-center font-medium text-muted-foreground" style={{ minWidth: 104 }}>Остаток плана</th>}
                    <th className="border-l border-border bg-primary/5 px-3 py-2 text-center font-semibold text-primary" style={{ minWidth: 108 }}>{periodStarted ? 'Итоговый план' : 'Итого план'}</th>
                    {periodStarted && <th className="border-l border-border bg-sky-50 px-3 py-2 text-center font-semibold text-sky-700" style={{ minWidth: 104 }}>Факт + прогноз</th>}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, ri) => {
                    if (row.type === 'group') return (
                      <GroupRow key={`g${ri}`} label={row.label}
                        colSpan={display.length + (periodStarted ? 5 : 2)}
                        collapsible={row.collapsible} expanded={row.expanded}
                        onToggle={row.collapsible ? () => setRegularExpanded((v) => !v) : undefined}
                        action={row.label === 'Воронка пробного' && canEditPlan ? (
                          <button
                            type="button"
                            data-tour="prefill"
                            onClick={prefillFromPreviousPeriodPlan}
                            disabled={isPrefillingPreviousPlan || selectedIndex <= 0 || editableWeekNumbers.length === 0}
                            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-2.5 py-1 text-[11px] font-semibold normal-case tracking-normal text-foreground shadow-sm hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
                            title={editableWeekNumbers.length === 0 ? 'В выбранном периоде нет редактируемых недель' : selectedIndex <= 0 ? 'Нет предыдущего периода' : 'Заполнить редактируемые недели данными плана прошлого периода'}
                          >
                            <ArrowRightLeft className="h-3.5 w-3.5" />
                            {isPrefillingPreviousPlan ? 'Загрузка…' : 'Предзаполнить данными плана прошлого периода'}
                          </button>
                        ) : row.label === 'Маркетинг и лиды' && canShowBulkPlanButton ? (
                          <button
                            type="button"
                            data-tour="bulk"
                            onClick={openBulkModal}
                            disabled={isDirty}
                            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-2.5 py-1 text-[11px] font-semibold normal-case tracking-normal text-primary-foreground shadow-sm hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
                            title={isDirty ? 'Сначала сохраните или отмените изменения сетки' : 'Распределить план лидов и маркетинга по студиям франчайзи'}
                          >
                            <Users className="h-3.5 w-3.5" />
                            Распределить маркетинг и лиды по студиям
                          </button>
                        ) : undefined} />
                    )
                    return (
                      <GridRow key={`r${ri}`} row={row} display={display} weeks={weeks} forecastForms={forecastForms}
                        targetWeekNumbers={targetWeekNumbers} scenario={scenario} tracking={periodStarted} today={today}
                        savedPlanTotal={totalForRow(row, savedAgg)} basePlanTotal={totalForRow(row, agg)} planTotal={totalForRow(row, activePlanAgg)}
                        factTotal={totalForRow(row, factTodayAgg)} forecastTotal={totalForRow(row, forecastAgg)}
                        additive={isAdditiveRow(row)} canCopyWeek1={canCopyWeek1} canEditPlan={canEditPlan}
                        hint={getRowHint(row, hints)} currencySym={currencySym}
                        onCopyField={copyFieldWeek1ToAll} onEdit={editCell} onAllocCellFocus={handleAllocCellFocus} />
                    )
                  })}
                </tbody>
              </table>
            </div>
          </Card>

          {/* ── Инспектор ── */}
          <div className="space-y-4">
            {periodStarted && (
            <Card className="p-4" data-tour="scenarios">
              <div className="mb-2 flex items-center gap-2 text-sm font-semibold"><TrendingUp className="h-4 w-4 text-primary" /> Сценарии</div>
              <p className="mb-3 text-xs text-muted-foreground">Меняют будущие недели ({fmtWeekNumbersText([...targetWeekNumbers])}). Завершённые и текущая — фиксируются.</p>
              <div className="space-y-2">
                <ScenarioBtn active={scenario === 'average'} disabled={!canEditPlan || targetWeekNumbers.size === 0} onClick={stageAverage} title="Средний факт" desc="Заполнить будущие недели средним темпом завершённых недель." />
                <ScenarioBtn active={scenario === 'reset'} disabled={!canEditPlan || targetWeekNumbers.size === 0} onClick={stageReset} title="Сбросить к плану периода" desc="Вернуть будущие недели к сохранённому плану." />
              </div>
              {preview && (
                <div className="mt-3 flex items-center gap-2">
                  <button onClick={applyScenario} className="flex-1 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90">Применить</button>
                  <button onClick={cancelScenario} className="flex items-center gap-1 rounded-lg border border-border px-3 py-2 text-sm hover:bg-muted"><RotateCcw className="h-3.5 w-3.5" /> Отмена</button>
                </div>
              )}
            </Card>
            )}

            {canEditPlan && (
              <div className="grid grid-cols-[1fr_auto] gap-2" data-tour="save">
                <button
                  onClick={handleSave}
                  disabled={!isDirty || isSaving}
                  className="flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground shadow-sm hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Save className="h-4 w-4" />
                  {isSaving ? 'Сохранение…' : isDirty ? 'Сохранить план' : 'Сохранено'}
                </button>
                <button
                  type="button"
                  onClick={resetUnsavedChanges}
                  disabled={!isDirty || isSaving}
                  className="flex items-center justify-center gap-1.5 rounded-lg border border-border px-3 py-2.5 text-sm font-semibold text-muted-foreground hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
                  title="Вернуть значения к сохранённому плану"
                >
                  <RotateCcw className="h-4 w-4" />
                  Сбросить
                </button>
              </div>
            )}

            <Card className="p-4" data-tour="summary">
              <div className="mb-3 flex items-center gap-2 text-sm font-semibold"><Target className="h-4 w-4 text-primary" /> Итог периода</div>
              {clientsAtMonthStartFact !== null && (
                <Stat label="База клиентов на начало периода" value={fmtInt(clientsAtMonthStartFact)} />
              )}
              <Stat label={periodPlanSummaryLabel} value={fmtMoney(activePlanAgg.totalRevenue, currencySym)} accent />
              {hasSavedPeriodPlan && hasPlanChangePreview && (
                <>
                  <Stat label="Сохранённая цель" value={fmtMoney(savedPeriodRevenue, currencySym)} />
                  <Stat label="Изменение к цели" value={`${activePlanAgg.totalRevenue - savedPeriodRevenue >= 0 ? '+' : ''}${fmtMoney(activePlanAgg.totalRevenue - savedPeriodRevenue, currencySym)}`} />
                </>
              )}
              {periodStarted && (
                <>
                  <div className="my-2 border-t border-border" />
                  <Stat label="Факт выручки к сегодня" value={fmtMoney(factTodayAgg.totalRevenue, currencySym)} />
                  {canCompareForecastToPlan && (
                    <Stat label="Осталось до текущего плана" value={fmtMoney(remainingToCurrentPlan, currencySym)} />
                  )}
                  <Stat label="Прогноз периода по факту" value={fmtMoney(forecastAgg.totalRevenue, currencySym)} accent />
                  {canCompareForecastToPlan && (
                    <Stat label="% прогноза к текущему плану" value={`${Math.round((forecastAgg.totalRevenue / activePlanAgg.totalRevenue) * 100)}%`} />
                  )}
                </>
              )}
            </Card>

            <Card className="p-4" data-tour="hints">
              <div className="mb-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Подсказки из истории</div>
              <p className="mb-3 text-xs text-muted-foreground">Недельные средние прошлого периода и за 3 месяца — отображаются в метке каждой строки сетки.</p>
              <button
                onClick={loadHints}
                disabled={hintsLoading}
                className="flex w-full items-center justify-center gap-2 rounded-lg border border-border px-3 py-2 text-sm hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
              >
                {hintsLoading ? 'Загрузка…' : hints ? 'Обновить подсказки' : 'Загрузить подсказки'}
              </button>
              {hints && (
                <p className="mt-2 text-[10px] leading-snug text-muted-foreground text-center">
                  Подсказки загружены · План и факт — средние прошлого периода, 3 мес — средний факт за 3 месяца.
                </p>
              )}
            </Card>
          </div>
        </div>
      </div>

      <BulkLeadPlanGridModal
        isOpen={showBulkModal}
        onClose={() => setShowBulkModal(false)}
        onSaved={() => setRefreshTick((t) => t + 1)}
        studios={partnerStudios}
        currentPartnerNumber={partnerNum}
        currentPeriodId={selectedPeriodId}
        currentYearNumber={selectedPeriod ? periodMidParts(selectedPeriod).y : null}
        currentMonthNumber={selectedPeriod ? periodMidParts(selectedPeriod).m : null}
        editableWeekNumbers={editableWeekNumbers}
        periodStart={selectedPeriod?.period_start ?? ''}
        periodLabel={periodLabel}
        userId={user?.id ?? null}
        currency={currencyCode}
        today={today}
        periodLenDays={selectedPeriod?.period_len_days ?? 28}
      />

      <OnboardingTour steps={WEEKLY_PLAN_TOUR_STEPS} open={tour.open} onClose={tour.close} />
    </div>
  )
}

// ── Под-компоненты ────────────────────────────────────────────────────────────
function GroupRow({ label, colSpan = 99, collapsible, expanded, onToggle, action }: { label: string; colSpan?: number; collapsible?: boolean; expanded?: boolean; onToggle?: () => void; action?: JSX.Element }) {
  if (collapsible) {
    return (
      <tr>
        <td colSpan={colSpan} className="bg-amber-50/70 p-0">
          <button type="button" onClick={onToggle} className="flex w-full items-center gap-2 px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-amber-900 hover:bg-amber-50">
            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${expanded ? '' : '-rotate-90'}`} />
            {label}
            <span className="ml-1 normal-case text-amber-900/60">{expanded ? '(скрыть)' : '(показать)'}</span>
          </button>
        </td>
      </tr>
    )
  }
  return (
    <tr>
      <td colSpan={colSpan} className="bg-muted/30 px-3 py-1.5">
        <div className="grid items-center gap-3" style={{ gridTemplateColumns: '216px max-content' }}>
          <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</span>
          <div>{action}</div>
        </div>
      </td>
    </tr>
  )
}

function CellDiff({ base, next, fmt, sym = '₽' }: { base: number; next: number; fmt: Fmt; sym?: string }) {
  if (round2(base) === round2(next)) return <span>{fmtBy(fmt, next, sym)}</span>
  return (
    <span className="inline-flex flex-col leading-tight">
      <span className="text-[10px] text-muted-foreground line-through">{fmtBy(fmt, base, sym)}</span>
      <span className="font-semibold text-sky-700">{fmtBy(fmt, next, sym)}</span>
    </span>
  )
}

interface GridRowProps {
  row: Extract<Row, { type: 'input' | 'knob' | 'derived' }>
  display: WeekDescriptor[]; weeks: WeekDescriptor[]; targetWeekNumbers: Set<number>
  forecastForms: PlanFormData[]
  scenario: ScenarioKey; tracking: boolean; today: string; savedPlanTotal: number; basePlanTotal: number; planTotal: number; factTotal: number; forecastTotal: number
  additive: boolean; canCopyWeek1: boolean; canEditPlan: boolean
  hint?: HintValues | null; currencySym: string
  onEdit: (week: number, field: keyof PlanFormData, value: number) => void
  onCopyField: (field: keyof PlanFormData) => void
  onAllocCellFocus?: () => void
}

// Ячейка ввода плана. Используем type="text" + inputMode вместо type="number", потому что:
//  1) нативные стрелки-спиннеры не нужны;
//  2) в вебвью Telegram поля type="number" часто «не печатаются» (выглядят неактивными).
// Локальный строковый черновик хранит промежуточный ввод («12.», «12,5»), иначе
// контролируемое числовое значение схлопывало бы дробную часть при наборе.
function PlanCellInput({ value, onEditValue, onFocus, className }: {
  value: number | undefined
  onEditValue: (n: number) => void
  onFocus?: () => void
  className?: string
}) {
  const [focused, setFocused] = useState(false)
  const [draft, setDraft] = useState('')
  const shown = focused ? draft : (value ? String(value) : '')
  return (
    <input
      type="text"
      inputMode="decimal"
      value={shown}
      onFocus={() => { setDraft(value ? String(value) : ''); setFocused(true); onFocus?.() }}
      onBlur={() => setFocused(false)}
      onChange={(e) => {
        // разрешаем только цифры и один десятичный разделитель (точку/запятую)
        const raw = e.target.value.replace(/[^\d.,]/g, '')
        setDraft(raw)
        const num = parseFloat(raw.replace(',', '.'))
        onEditValue(Number.isFinite(num) ? num : 0)
      }}
      className={className}
    />
  )
}

function GridRow({ row, display, weeks, targetWeekNumbers, forecastForms, scenario, tracking, today, savedPlanTotal, basePlanTotal, planTotal, factTotal, forecastTotal, additive, canCopyWeek1, canEditPlan, hint, currencySym, onEdit, onCopyField, onAllocCellFocus }: GridRowProps) {
  const isKnob = row.type === 'knob'
  const isDerived = row.type === 'derived'
  const highlight = isDerived && row.highlight
  const scenarioActive = Boolean(scenario)
  // Остаток плана = Итоговый план − Факт. База (до сценария) от текущего плана, next — от сценарного.
  const remainingBase = round2(basePlanTotal - factTotal)
  const remainingNext = round2(planTotal - factTotal)
  const sel = (f: PlanFormData): number => isDerived ? (derive(f) as any)[row.key] as number : f[row.field]
  const labelCls = isKnob
    ? 'sticky left-0 z-10 bg-muted/10 py-1 pl-7 pr-3 text-xs italic text-muted-foreground'
    : highlight
      ? 'sticky left-0 z-10 bg-primary/5 px-3 py-2 font-semibold'
      : isDerived
        ? 'sticky left-0 z-10 bg-background px-3 py-2 text-muted-foreground'
        : 'sticky left-0 z-10 bg-background px-3 py-2 font-medium'
  const rowCls = isKnob ? 'border-b border-dashed border-border/40 bg-muted/10'
    : highlight ? 'border-b border-border/60 bg-primary/5 font-semibold'
      : 'border-b border-border/60 hover:bg-muted/20'
  const totalCls = (extra: string) => `px-3 ${isKnob ? 'py-1 text-xs' : 'py-2'} text-center ${extra}`
  const pref = isKnob ? '⌀ ' : ''

  return (
    <tr className={rowCls}>
      <td className={labelCls}>
        <div>{isKnob ? `↳ ${row.label}` : row.label}</div>
        {hint && (
          <div className="mt-1 flex max-w-[240px] flex-wrap gap-1 text-[10px] leading-none">
            <span className="inline-flex items-center gap-1 rounded border border-border/70 bg-muted/60 px-1.5 py-1 text-foreground shadow-sm">
              <span className="font-medium text-muted-foreground">План</span>
              <span>{hint.plan !== null ? fmtBy(row.fmt, hint.plan, currencySym) : '—'}</span>
            </span>
            <span className="inline-flex items-center gap-1 rounded border border-border/70 bg-muted/60 px-1.5 py-1 text-foreground shadow-sm">
              <span className="font-medium text-muted-foreground">Факт</span>
              <span>{hint.fact !== null ? fmtBy(row.fmt, hint.fact, currencySym) : '—'}</span>
            </span>
            <span className="inline-flex items-center gap-1 rounded border border-border/70 bg-muted/60 px-1.5 py-1 text-foreground shadow-sm">
              <span className="font-medium text-muted-foreground">3 мес</span>
              <span>{hint.fact3mo !== null ? fmtBy(row.fmt, hint.fact3mo, currencySym) : '—'}</span>
            </span>
          </div>
        )}
      </td>
      {display.map((w, i) => {
        const isTarget = scenarioActive && targetWeekNumbers.has(w.weekNumber)
        const isTemplate = !tracking && w.weekNumber === 1
        // Лиды/бюджет «из распределения по студиям» — подсветка (P2-2, soft signal).
        const isAllocCell = row.type === 'input' && (row.field === 'leadsCount' || row.field === 'marketingBudget') && w.planSource === 'partner_allocation'
        const cellCls = `relative px-2 ${isKnob ? 'py-1' : 'py-2'} text-center ${isAllocCell ? 'bg-violet-50/60' : isTemplate ? 'bg-primary/5' : isTarget ? 'bg-sky-50' : ''}`
        const editable = isWeekEditable(w, today)
        const planV = sel(w.formData)
        const bottomV = sel(forecastForms[i] ?? w.formData)
        // нижняя линия: факт (завершённые) или прогноз (текущая/будущие)
        const bottomCls = w.isCompleted ? 'text-emerald-700' : 'text-sky-600'
        const bottomLabel = w.isCompleted ? '' : '~'
        const showBottomLine = tracking && !(scenario === 'average' && targetWeekNumbers.has(w.weekNumber))

        // верхняя линия (план)
        let topEl: JSX.Element
        if (scenarioActive && !isDerived) {
          topEl = <CellDiff base={weeks[i]?.formData[row.field] ?? 0} next={w.formData[row.field]} fmt={row.fmt} sym={currencySym} />
        } else if (!isDerived && editable && !scenarioActive && canEditPlan) {
          topEl = <PlanCellInput value={w.formData[row.field]} onEditValue={(v) => onEdit(w.weekNumber, row.field, v)}
            onFocus={isAllocCell ? onAllocCellFocus : undefined}
            className={`w-full rounded border bg-background px-1 py-0.5 text-center focus:border-primary focus:outline-none ${isKnob ? 'border-border/40 text-xs' : 'border-border/60 text-sm'}`} />
        } else {
          topEl = <span className={isKnob ? 'text-xs' : ''}>{fmtBy(row.fmt, planV, currencySym)}</span>
        }

        return (
          <td key={w.weekNumber} className={cellCls}>
            <div className="flex flex-col leading-tight">
              {canCopyWeek1 && w.weekNumber === 1 && (row.type === 'input' || row.type === 'knob') ? (
                <div className="flex items-center gap-0.5">
                  <div className="flex-1">{topEl}</div>
                  <button onClick={() => onCopyField(row.field)} title="Скопировать значение недели 1 на остальные недели"
                    className="shrink-0 rounded p-0.5 text-primary/60 hover:bg-primary/10 hover:text-primary">
                    <ChevronsRight className="h-3 w-3" />
                  </button>
                </div>
              ) : topEl}
              {showBottomLine && <span className={`text-[10px] ${bottomCls}`}>{bottomLabel}{fmtBy(row.fmt, bottomV, currencySym)}</span>}
            </div>
          </td>
        )
      })}
      {tracking && <td className={`border-l border-border ${totalCls('text-emerald-700')}`}>{pref}{fmtBy(row.fmt, factTotal, currencySym)}</td>}
      {tracking && (
        <td className={totalCls('text-muted-foreground')}>
          {additive
            ? (scenarioActive ? <CellDiff base={remainingBase} next={remainingNext} fmt={row.fmt} sym={currencySym} /> : fmtBy(row.fmt, remainingNext, currencySym))
            : <span className="text-muted-foreground/30">—</span>}
        </td>
      )}
      <td className={`border-l border-border ${totalCls(highlight ? 'bg-primary/10 text-primary font-semibold' : 'bg-primary/5 font-semibold text-primary')}`}>
        {pref}{scenarioActive ? <CellDiff base={savedPlanTotal} next={planTotal} fmt={row.fmt} sym={currencySym} /> : fmtBy(row.fmt, planTotal, currencySym)}
      </td>
      {tracking && <td className={`border-l border-border ${totalCls(highlight ? 'bg-sky-100 text-sky-700 font-semibold' : 'bg-sky-50 font-semibold text-sky-700')}`}>{pref}{fmtBy(row.fmt, forecastTotal, currencySym)}</td>}
    </tr>
  )
}

function ScenarioBtn({ active, disabled, onClick, title, desc }: { active: boolean; disabled?: boolean; onClick: () => void; title: string; desc: string }) {
  return (
    <button onClick={onClick} disabled={disabled}
      className={`w-full rounded-xl border px-3 py-2 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${active ? 'border-primary/60 bg-primary/5 ring-1 ring-primary/20' : 'border-border/80 bg-background hover:border-primary/30 hover:bg-muted/30'}`}>
      <div className="text-sm font-semibold">{title}</div>
      <div className="mt-0.5 text-xs leading-4 text-muted-foreground">{desc}</div>
    </button>
  )
}

function Stat({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className="flex items-baseline justify-between py-0.5">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className={`text-sm font-semibold ${accent ? 'text-primary' : ''}`}>{value}</span>
    </div>
  )
}
